import React, { useEffect, useRef, useState } from 'react'
import JournalTab      from './JournalTab'
import DemoTopicsTab   from './DemoTopicsTab'
import MentorSessionTab from './MentorSessionTab'
import Markdown from '../../components/Markdown'
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, LinearProgress, Tab,
  Tabs, TextField, Typography,
} from '@mui/material'
import ArrowBackIcon    from '@mui/icons-material/ArrowBack'
import CheckCircleIcon  from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined'
import ThumbUpIcon      from '@mui/icons-material/ThumbUp'
import ThumbDownIcon    from '@mui/icons-material/ThumbDown'
import ExpandMoreIcon   from '@mui/icons-material/ExpandMore'
import ExpandLessIcon   from '@mui/icons-material/ExpandLess'
import PrintIcon        from '@mui/icons-material/Print'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'
import { useAuth } from '../../auth/AuthContext'
import { useViewerStudent } from '../../auth/ViewAsContext'
import { Project } from '../../types'
import StepReflectionCard from '../../components/journal/StepReflectionCard'
import SaveToJournalButton from '../../components/journal/SaveToJournalButton'


// Vimeo video IDs from ardent-api Lesson 1 problem sets (DISPLAY problems)
const LEARN_VIDEOS = [
  { title: 'Introduction to Science Research',          videoId: '845600712' },
  { title: 'Scientific Inquiry vs. Engineering Design', videoId: '845600809' },
  { title: 'Prepare Your Research Notebook',            videoId: '845600836' },
  { title: 'Select a Research Topic',                   videoId: '845600890' },
  { title: 'Brainstorm Project Topics',                 videoId: '799963284' },
  { title: 'The Research Journey of Pranav Moudgalya', videoId: '852515724' },
]

interface Summary {
  confirmed_topic:        string
  confirmed_track:        string
  confirmed_project_type: string
  confirmed_scope:        string
  confirmed_intent:       string
  ai_rationale:           string
  step2_preview:          string
}

interface CuratedCategory {
  id:                string
  name:              string
  short_description: string
  long_description:  string | null
  example_projects:  string[] | null
  related_subjects:  string[] | null
  display_order:     number
}

interface SelectedCategory {
  category: CuratedCategory
  reason:   string
}

interface TopicRec {
  id:                  string
  round:               number
  title:               string
  description:         string
  spanning_categories: string[] | null
  decision:            'accept' | 'reject' | null
  justification:       string | null
  source?:             'ai' | 'student' | 'demo'
  evaluation?:         Record<string, any> | null
}


interface ProjectDirection {
  id:               string
  cell:             'scientific_experimental' | 'scientific_computational' | 'engineering_experimental' | 'engineering_computational'
  title:            string
  summary:          string
  core_question:    string | null
  what_you_do:      string | null
  tools_materials:  string | null
  data_format:      string | null
  finished_project: string | null
}

type ResearchPhase =
  | 'loading'
  | 'category'
  | 'gate_failed'
  | 'gate_passed'
  | 'loop'
  | 'comparison'
  | 'final'
  | 'direction'
  | 'submitted'
  | 'completed'

// ── Learn tab ─────────────────────────────────────────────────────────────────

// ── 5-dim feasibility row for student-proposed topic evaluation ──────────────
const DIM_LABELS: Record<string, string> = {
  time_fit:    'Time fit',
  budget:      'Budget',
  equipment:   'Equipment',
  skills:      'Skills',
  specificity: 'Specificity',
}
function DimRow({ dimKey, d }: { dimKey: string; d: { state: string; note: string } }) {
  const mark =
    d.state === 'ok'      ? '✓'
    : d.state === 'caution' ? '⚠'
    : '?'
  const color =
    d.state === 'ok'      ? '#2e7d32'
    : d.state === 'caution' ? '#ed6c02'
    : '#1976d2'
  const bg =
    d.state === 'ok'      ? 'rgba(46,125,50,0.06)'
    : d.state === 'caution' ? 'rgba(237,108,2,0.06)'
    : 'rgba(25,118,210,0.06)'
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.25,
      mb: 1, p: 1.25, borderRadius: 1.5,
      bgcolor: bg, border: `1px solid ${color}30`,
    }}>
      <Box sx={{
        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
        bgcolor: color, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.85rem', fontWeight: 800, lineHeight: 1,
      }}>{mark}</Box>
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color, display: 'block' }}>
          {DIM_LABELS[dimKey] ?? dimKey}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
          {d.note}
        </Typography>
      </Box>
    </Box>
  )
}

// Example research questions the topic could lead to — a "this is fertile" signal
// at the topic stage. Directions to choose from in Step 2, not assignments.
function ResearchQuestions({ questions }: { questions: string[] }) {
  const list = (questions ?? []).filter(q => (q ?? '').trim())
  if (list.length === 0) return null
  return (
    <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(25,118,210,0.05)', border: '1px solid rgba(25,118,210,0.18)' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976d2', display: 'block', mb: 0.5 }}>
        Questions this topic could lead to
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {list.map((q, i) => (
          <Typography key={i} component="li" variant="caption" color="text.secondary" sx={{ display: 'list-item', lineHeight: 1.5, mb: 0.25 }}>
            {q}
          </Typography>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5, opacity: 0.85 }}>
        You'll pick one and sharpen it in Step 2 — you don't need to decide yet.
      </Typography>
    </Box>
  )
}

function VimeoEmbed({ videoId, title }: { videoId: string; title: string }) {
  return (
    <Box sx={{ textAlign: 'center', mt: 2 }}>
      <iframe
        title={title}
        src={`https://player.vimeo.com/video/${videoId}?title=0&portrait=0&byline=0`}
        width="640"
        height="360"
        style={{ border: 'none', maxWidth: '100%' }}
        allow="autoplay; fullscreen"
        allowFullScreen
      />
    </Box>
  )
}

function LearnTab({ studentId }: { studentId: string }) {
  const [activeVideo, setActiveVideo] = useState<number | null>(null)
  const [ackAt,       setAckAt]       = useState<string | null>(null)
  const [ackLoading,  setAckLoading]  = useState(true)
  const [ackChecked,  setAckChecked]  = useState(false)
  const [acking,      setAcking]      = useState(false)

  useEffect(() => {
    let cancelled = false
    supabase
      .from('student_profile')
      .select('ai_ethics_ack_at')
      .eq('student_id', studentId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return
        const at = (data as { ai_ethics_ack_at?: string | null } | null)?.ai_ethics_ack_at ?? null
        setAckAt(at)
        setAckChecked(!!at)
        setAckLoading(false)
      })
    return () => { cancelled = true }
  }, [studentId])

  async function handleAcknowledge() {
    if (!ackChecked || ackAt) return
    setAcking(true)
    const now = new Date().toISOString()
    const { error } = await supabase
      .from('student_profile')
      .update({ ai_ethics_ack_at: now })
      .eq('student_id', studentId)
    if (!error) setAckAt(now)
    setAcking(false)
  }

  return (
    <Box sx={{ maxWidth: 800, py: 2 }}>
      {/* Welcoming hero */}
      <Box sx={{
        mb: 3, p: 3, borderRadius: 2,
        bgcolor: 'primary.main', color: '#fff',
      }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
          Welcome to your research journey.
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Real research starts with curiosity and a clear plan. Watch the lessons below to learn
          how scientists and engineers approach the work — then bring your own questions to Step 1.
        </Typography>
      </Box>

      {/* What you'll learn */}
      <Box sx={{ mb: 3, p: 2.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>What you'll learn</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
          <Typography component="li" variant="body2" color="text.secondary">
            The difference between scientific inquiry and engineering design — and how to choose between them
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            How to brainstorm topics that are <em>specific, connected, and feasible</em>
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            How real student researchers turned curiosity into competition-winning projects
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            How to keep a research notebook that captures your thinking
          </Typography>
        </Box>
      </Box>

      {/* Using AI in your research — teaching only; Step 1 itself is unchanged */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Using AI in your research</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>
            Start with the subject you're curious about — that's your research category. AI and machine
            learning are just one <strong>way to investigate</strong> it: instead of a hands-on experiment,
            you can answer your question by building a model on a <strong>dataset</strong>. In
            {' '}<strong>Medicine</strong>, for example, you might use a breast-cancer dataset to predict a diagnosis.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            If the <strong>AI itself is the point</strong> of your project — the algorithm, not a medical or
            environmental question — you'll compete in the <strong>Computer Science</strong> category instead.
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Choosing a dataset — what to look for</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5, '& li': { mb: 0.25 } }}>
            <Typography component="li" variant="body2" color="text.secondary">
              A clear <strong>target</strong> to predict, plus useful <strong>features</strong> (the inputs)
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Enough rows, and reasonably <strong>clean and labeled</strong>
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              <strong>Public and licensed</strong> for use — no private or personal data (keeps it safe for the fair's review)
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Actually <strong>relevant</strong> to a question you care about
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            New to this? Practice the whole process on a sample dataset in the <strong>AI Lab (Beta)</strong> — in the left menu.
          </Typography>
        </CardContent>
      </Card>

      {/* Lessons */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Lessons</Typography>
      {LEARN_VIDEOS.map((v, i) => (
        <Card key={v.videoId} variant="outlined" sx={{ mb: 1.5 }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => setActiveVideo(activeVideo === i ? null : i)}
            >
              <PlayCircleOutlinedIcon color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                {i + 1}. {v.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {activeVideo === i ? 'Hide' : 'Watch'}
              </Typography>
            </Box>
            {activeVideo === i && <VimeoEmbed videoId={v.videoId} title={v.title} />}
          </CardContent>
        </Card>
      ))}

      {/* Ethical Use of AI — commitment block */}
      <Box sx={{
        mt: 4, p: 3, borderRadius: 2,
        border: '1px solid', borderColor: ackAt ? 'success.light' : 'warning.light',
        bgcolor: ackAt ? 'success.lighter' : 'warning.lighter',
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Ethical Use of AI in Your Research
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Your AI Research Coach is here to help you <em>think</em> — not to do the thinking for you.
          Judges and mentors want to see <em>your</em> reasoning, your decisions, and your voice in the work.
        </Typography>
        <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.5, '& li': { mb: 0.5 } }}>
          <Typography component="li" variant="body2">
            <strong>Use AI to:</strong> brainstorm directions, clarify concepts, find background sources,
            explain things you don't understand, and stress-test your ideas.
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Don't use AI to:</strong> write your hypothesis, design your experiment, analyze
            your data, or draft your final report in your place.
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Always document</strong> how you used AI in your research notebook — what you
            asked, what it suggested, and what <em>you</em> decided.
          </Typography>
        </Box>

        {ackLoading ? null : ackAt ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            <Typography variant="caption" color="success.dark" sx={{ fontWeight: 600 }}>
              Acknowledged on {new Date(ackAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={ackChecked}
                  onChange={e => setAckChecked(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  I understand and agree to use AI responsibly throughout my research project.
                </Typography>
              }
            />
            <Box>
              <Button
                variant="contained" size="small"
                disabled={!ackChecked || acking}
                onClick={handleAcknowledge}
              >
                {acking ? <CircularProgress size={18} /> : 'I acknowledge'}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

// ── Research Category tab ──────────────────────────────────────────────────────

// activeView: 'category' renders the category browser/summary
//             'topic'    renders the loop/comparison/final/submitted/completed phases
interface GateScore {
  name:        string
  specificity: number   // 1–4
  connection:  number   // 1–3
  effort:      number   // 1–3
  total:       number   // max 10
}

function ResearchFlow({
  projectId, studentId, activeView, onGatePassed, onAdvanceToTopic, isTrial,
}: {
  projectId:        string
  studentId:        string
  activeView:       'category' | 'topic'
  onGatePassed:     () => void   // just unlocks tabs — does NOT switch tab
  onAdvanceToTopic: () => void   // called when student clicks Continue in the review
  isTrial?:         boolean
}) {
  const navigate = useNavigate()
  const { prefixPath, viewerIsStudent } = useViewerStudent()
  const [phase,          setPhase]          = useState<ResearchPhase>('loading')
  const [categories,     setCategories]     = useState<CuratedCategory[]>([])
  const [selections,     setSelections]     = useState<SelectedCategory[]>([])
  const [expanded,       setExpanded]       = useState<string | null>(null)
  const [gateFeedback,   setGateFeedback]   = useState<string | null>(null)
  const [gateScores,     setGateScores]     = useState<GateScore[]>([])
  const [prevGateScores, setPrevGateScores] = useState<GateScore[]>([])
  const [recommendations,setRecommendations]= useState<TopicRec[]>([])
  const [, setComparison]     = useState<any | null>(null)
  const [finalPickId,    setFinalPickId]    = useState<string | null>(null)
  const [finalJustify,   setFinalJustify]   = useState('')
  // New finalists workflow: multi-select pre-submission, status of the
  // submitted research_topic, and the most recent mentor note (if any).
  const [pickedFinalistIds, setPickedFinalistIds] = useState<string[]>([])
  const [finalistThoughts,  setFinalistThoughts]  = useState<Record<string, string>>({})
  const [submissionStatus,  setSubmissionStatus]  = useState<'draft' | 'submitted' | 'completed' | null>(null)
  const [step2Approved,     setStep2Approved]     = useState(false)
  const [meetingHeldAt,     setMeetingHeldAt]     = useState<string | null>(null)
  const [meetingRequestedAt, setMeetingRequestedAt] = useState<string | null>(null)
  const [meetingScheduledAt, setMeetingScheduledAt] = useState<string | null>(null)
  const [submitting,        setSubmitting]        = useState(false)
  const [reflectionDone,    setReflectionDone]    = useState(false)
  const [advancing,         setAdvancing]         = useState(false)
  const [mentorNote,     setMentorNote]     = useState<string | null>(null)
  const [deciding,       setDeciding]       = useState<string | null>(null)  // rec id being decided
  const [decidingAction, setDecidingAction] = useState<'accept' | 'reject' | null>(null)
  const [decisionText,   setDecisionText]   = useState('')
  const [loading,        setLoading]        = useState(false)
  const [error,          setError]          = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  // Per-topic inline Q&A (keyed by topic id, session-only — not persisted)
  const [askingTopicId,  setAskingTopicId]  = useState<string | null>(null)
  const [askInput,       setAskInput]       = useState('')
  const [askLoading,     setAskLoading]     = useState(false)
  const [askHistory,     setAskHistory]     = useState<Record<string, Array<{question: string; answer: string}>>>({})
  const [studentGrade,        setStudentGrade]        = useState<string | null>(null)
  // Direction discovery
  const [directions,          setDirections]          = useState<ProjectDirection[]>([])
  const [directionLoading,    setDirectionLoading]    = useState(false)
  const [directionError,      setDirectionError]      = useState<string | null>(null)
  const [expandedCell,        setExpandedCell]        = useState<string | null>(null)
  const [dirAskCell,          setDirAskCell]          = useState<string | null>(null)
  const [dirAskInput,         setDirAskInput]         = useState('')
  const [dirAskLoading,       setDirAskLoading]       = useState(false)
  const [dirAskHistory,       setDirAskHistory]       = useState<Record<string, Array<{question: string; answer: string}>>>({})
  const [committingDirection, setCommittingDirection] = useState(false)
  const [exampleOpen,         setExampleOpen]         = useState(false)
  const [aiSuggestionsOpen,   setAiSuggestionsOpen]   = useState(false)
  const [overruleOpen,        setOverruleOpen]        = useState<{ kind: 'student' | 'ai'; rec: TopicRec | null; states: string[] } | null>(null)
  const [needStudentAuthored, setNeedStudentAuthored] = useState(false)
  const [wonderPrompts,       setWonderPrompts]       = useState<string[] | null>(null)
  const [wonderLoading,       setWonderLoading]       = useState(false)
  const [activeCategoryId,    setActiveCategoryId]    = useState<string>('')
  const [examplesOpen,        setExamplesOpen]        = useState(false)
  const [reconsiderTarget,    setReconsiderTarget]    = useState<TopicRec | null>(null)
  const [evaluatingTopicId,   setEvaluatingTopicId]   = useState<string | null>(null)
  // Student-proposed topic
  const [, setProposing]         = useState(false)
  const [proposeTitle,      setProposeTitle]       = useState('')
  const [proposeDesc,       setProposeDesc]        = useState('')
  const [proposeLoading,    setProposeLoading]     = useState(false)
  const [proposeEval,       setProposeEval]        = useState<any | null>(null)   // evaluation result
  const [proposedRec,       setProposedRec]        = useState<TopicRec | null>(null) // inserted rec row
  // Final-phase propose: track which existing topic the student wants to replace
  const [, setFinalProposing]     = useState(false)
  const [awaitingReplaceFor,setAwaitingReplaceFor] = useState<TopicRec | null>(null) // proposed rec waiting for a swap target

  const acceptedTopics = recommendations.filter(r => r.decision === 'accept')
  const pendingRecs    = recommendations.filter(r => r.decision === null)
  // currentRec is the first pending suggestion. When a category is active we
  // prefer suggestions tagged for it, falling back to the global queue.
  const activeCategoryName = selections.find(s => s.category.id === activeCategoryId)?.category.name
  const currentRec     = (activeCategoryName
                            ? pendingRecs.find(r => r.spanning_categories?.includes(activeCategoryName))
                            : null) ?? pendingRecs[0] ?? null

  // ── Load state on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      const [
        { data: cats, error: catErr },
        { data: ci,   error: ciErr  },
        { data: recs, error: recErr },
        { data: rt,   error: rtErr  },
        { data: sp },
        { data: rq },
      ] = await Promise.all([
        supabase.from('curated_category').select('*').order('display_order'),
        supabase.from('category_input').select('*').eq('project_id', projectId).maybeSingle(),
        supabase.from('topic_recommendation').select('*')
          .eq('project_id', projectId).order('generated_at'),
        supabase.from('research_topic').select('*')
          .eq('project_id', projectId).maybeSingle(),
        supabase.from('student_profile').select('grade').eq('student_id', studentId).maybeSingle(),
        supabase.from('research_question').select('status').eq('project_id', projectId).maybeSingle(),
      ])
      if (sp?.grade) setStudentGrade(sp.grade)
      setStep2Approved(rq?.status === 'approved_draft' || rq?.status === 'completed')

      const loadErrors = [
        catErr  && `categories: ${catErr.message}`,
        ciErr   && `category_input: ${ciErr.message}`,
        recErr  && `recommendations: ${recErr.message}`,
        rtErr   && `research_topic: ${rtErr.message}`,
      ].filter(Boolean)
      if (loadErrors.length) {
        setError(`Load error — ${loadErrors.join(' | ')}`)
        setPhase('category')
        return
      }

      setCategories(cats ?? [])
      setRecommendations((recs ?? []) as TopicRec[])

      // Restore per-topic finalist thoughts from any prior submissions
      const thoughtsMap: Record<string, string> = {}
      for (const r of recs ?? []) {
        const ft = (r as any).finalist_thoughts as string | null
        if (ft) thoughtsMap[r.id] = ft
      }
      setFinalistThoughts(thoughtsMap)

      // Always restore selections — must happen before early returns below
      if (ci?.selections?.length) {
        const sels: SelectedCategory[] = (ci.selections as any[]).map((s: any) => ({
          category: cats?.find(c => c.id === s.category_id) ?? { id: s.category_id, name: s.name, short_description: '', long_description: null, example_projects: null, related_subjects: null, display_order: 0 },
          reason: s.reason,
        }))
        setSelections(sels)
        if (sels.length > 0 && !activeCategoryId) setActiveCategoryId(sels[0].category.id)
      }

      if (rt) {
        setComparison(rt.ai_comparison)
        setFinalPickId(rt.selected_recommendation_id)
        // student_thoughts is the new field; final_justification is legacy
        const thoughts = (rt as any).student_thoughts ?? rt.final_justification ?? ''
        setFinalJustify(thoughts)
        // Only restore finalists that are STILL accepted. A prior finalist the
        // student later un-accepted (reconsidered) lingers in
        // finalist_recommendation_ids; re-submitting it would fail the
        // "not in your accepted shortlist" check in step1-submit-finalists.
        const acceptedIds = new Set((recs ?? []).filter((r: any) => r.decision === 'accept').map((r: any) => r.id))
        setPickedFinalistIds((((rt as any).finalist_recommendation_ids ?? []) as string[]).filter(id => acceptedIds.has(id)))
        setSubmissionStatus(rt.status)
        setMentorNote(rt.mentor_note ?? null)

        // Meeting state lives on the meeting table now. We need both:
        //   - any active step1_review meeting (requested/scheduled) for the
        //     "awaiting review" sub-states
        //   - the most recent held step1_review meeting for the "Notes from
        //     your meeting" returning-from-review banner
        const [{ data: active }, { data: lastHeld }] = await Promise.all([
          supabase
            .from('meeting')
            .select('scheduled_at, requested_at, status')
            .eq('research_topic_id', rt.id)
            .eq('kind', 'step1_review')
            .in('status', ['requested', 'scheduled'])
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from('meeting')
            .select('held_at')
            .eq('research_topic_id', rt.id)
            .eq('kind', 'step1_review')
            .eq('status', 'held')
            .order('held_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
        ])
        setMeetingScheduledAt((active as any)?.scheduled_at ?? null)
        setMeetingRequestedAt((active as any)?.requested_at ?? null)
        setMeetingHeldAt((lastHeld as any)?.held_at ?? null)

        if (rt.status === 'completed') { setPhase('completed'); return }
        // Both 'draft' and 'submitted' route to the final-pick screen; the
        // screen renders status-aware variants (multi-select form vs.
        // read-only awaiting banner).
      }

      if (!ci || !ci.gate_passed) {
        if (ci && !ci.gate_passed && ci.gate_feedback) {
          setGateFeedback(ci.gate_feedback)
          if (ci.gate_scores?.length) setGateScores(ci.gate_scores as GateScore[])
          setPhase('gate_failed')
        } else {
          setPhase('category')
        }
        return
      }

      // Gate passed — restore scores from DB so re-check can show deltas
      if (ci.gate_scores?.length) setGateScores(ci.gate_scores as GateScore[])

      // Trial students stop at the gate — restore the trial completion review
      if (isTrial) { setPhase('gate_passed'); return }

      // Gate passed — determine phase by accepted topic count.
      const accepted = (recs ?? []).filter((r: any) => r.decision === 'accept')
      // A student with their own topic may submit it alone, so a submission can
      // exist with fewer than 3 accepted — it must still land on that screen.
      const hasSubmission = !!rt && (rt.status === 'submitted' || ((rt as any).finalist_recommendation_ids ?? []).length > 0)
      if (accepted.length >= 3 || hasSubmission) {
        // Final-pick screen renders draft/submitted/completed variants based on submissionStatus.
        setPhase('final')
      } else {
        setPhase('loop')
        // Auto-fetch first batch if no recs yet
        if (!recs?.length) fetchRecommendations()
      }
    }
    load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  // ── Gate submission ──────────────────────────────────────────────────────────
  async function handleGateSubmit() {
    if (selections.length < 1 || selections.some(s => s.reason.trim().length < 30)) return
    setLoading(true)
    setError(null)
    try {
      const body = {
        project_id: projectId,
        student_id: studentId,
        is_trial:   isTrial,
        selections: selections.map(s => ({
          category_id: s.category.id,
          name:        s.category.name,
          reason:      s.reason.trim(),
        })),
      }
      const { data, error: invokeErr } = await supabase.functions.invoke('gate-category-input', { body })
      console.debug('[gate]', invokeErr, data)

      if (invokeErr || data?.error) {
        setError(`Gate check failed: ${invokeErr?.message ?? data?.error ?? 'unknown error'}`)
        return
      }
      if (data.passed) {
        setGateFeedback(null)
        setPrevGateScores(gateScores)
        setGateScores(data.scores ?? [])
        setPhase('gate_passed')
        onGatePassed()   // unlocks tabs only — does NOT switch tab
      } else {
        setGateFeedback(data.feedback ?? null)
        setPrevGateScores(gateScores)
        setGateScores(data.scores ?? [])
        setPhase('gate_failed')
      }
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // ── Fetch topic recommendations ──────────────────────────────────────────────
  async function fetchRecommendations(mode: 'fresh' | 'harder' | 'simpler' | 'different_angle' = 'fresh', baseTopicId?: string) {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    setLoading(true)
    setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('recommend-topics', {
        body: {
          project_id:    projectId,
          student_id:    studentId,
          mode,
          base_topic_id: baseTopicId,
          focus_category_name: activeCategoryName ?? null,
        },
      })
      if (ctrl.signal.aborted) return
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      // Use rows returned directly — no second Supabase round-trip needed
      setRecommendations(prev => {
        const newIds = new Set((data.topics ?? []).map((t: any) => t.id))
        const existing = prev.filter(r => !newIds.has(r.id))
        return [...existing, ...(data.topics ?? [])] as TopicRec[]
      })
    } catch (e: any) {
      if (ctrl.signal.aborted || e.name === 'AbortError') return
      setError(e.message)
    } finally {
      if (!ctrl.signal.aborted) setLoading(false)
    }
  }

  // ── Iterate on the current AI suggestion (4-button modes) ──────────────────
  // Auto-rejects the current rec with a system justification, then fetches a
  // variant from recommend-topics with the chosen mode + base topic.
  async function iterateOnTopic(rec: TopicRec, mode: 'fresh' | 'harder' | 'simpler' | 'different_angle') {
    setLoading(true)
    setError(null)
    try {
      const justification =
        mode === 'fresh'           ? 'Asked AI for a fresh idea'
        : mode === 'harder'        ? 'Asked AI for a harder version'
        : mode === 'simpler'       ? 'Asked AI for a simpler version'
        : /* different_angle */     'Asked AI for a different angle'
      await supabase.from('topic_recommendation').update({
        decision:      'reject',
        justification,
        decided_at:    new Date().toISOString(),
      }).eq('id', rec.id)
      const updated = recommendations.map(r =>
        r.id === rec.id ? { ...r, decision: 'reject' as const, justification } : r
      )
      setRecommendations(updated)
      // Fetch variant
      await fetchRecommendations(mode, mode === 'fresh' ? undefined : rec.id)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // ── Wonder prompts ("I'm stuck — give me ideas to wonder about") ───────────
  async function fetchWonderPrompts() {
    setWonderLoading(true)
    setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('recommend-topics', {
        body: {
          project_id: projectId,
          student_id: studentId,
          mode:       'wonder',
          focus_category_name: activeCategoryName ?? null,
        },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      setWonderPrompts(data?.wonder_prompts ?? [])
    } catch (e: any) {
      setError(e.message)
    } finally {
      setWonderLoading(false)
    }
  }

  // ── Accept / Reject a recommendation ────────────────────────────────────────
  async function handleDecision(rec: TopicRec, decision: 'accept' | 'reject') {
    if (!decisionText.trim()) return
    setLoading(true)
    try {
      await supabase.from('topic_recommendation').update({
        decision,
        justification: decisionText.trim(),
        decided_at:    new Date().toISOString(),
      }).eq('id', rec.id)

      const updated = recommendations.map(r =>
        r.id === rec.id ? { ...r, decision, justification: decisionText.trim() } : r
      )
      setRecommendations(updated)
      setDeciding(null)
      setDecidingAction(null)
      setDecisionText('')

      const newAccepted = updated.filter(r => r.decision === 'accept')
      if (newAccepted.length >= 3) {
        const studentCount = newAccepted.filter(r => r.source === 'student').length
        if (studentCount === 0) {
          setNeedStudentAuthored(true)
        } else {
          // 3 accepts → final-pick screen (no AI comparison step anymore).
          setPhase('final')
          setSubmissionStatus('draft')
        }
      } else if (updated.filter(r => r.decision === null).length === 0) {
        // batch exhausted — fetch next batch immediately
        fetchRecommendations()
      }
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // ── Accept a student-proposed topic (after AI evaluation) ────────────────────
  // `alreadyAccepted` skips the frontend DB write when the row was already
  // accepted server-side (the no-review path goes through the edge function,
  // which sets decision='accept' with the service-role key — a frontend write
  // here would fail RLS in staff View-As sessions).
  async function acceptProposed(rec: TopicRec, alreadyAccepted = false) {
    if (!alreadyAccepted) {
      await supabase.from('topic_recommendation').update({
        decision:      'accept',
        justification: 'My own idea',
        decided_at:    new Date().toISOString(),
      }).eq('id', rec.id)
    }
    const accepted = { ...rec, decision: 'accept' as const, justification: 'My own idea' }
    const updated  = [...recommendations, accepted]
    setRecommendations(updated)
    resetPropose()
    const newAccepted = updated.filter(r => r.decision === 'accept')
    if (newAccepted.length >= 3) {
      // Student-proposed accept always satisfies the ≥1-student-authored rule.
      // No AI comparison step — go straight to final-pick.
      setPhase('final')
      setSubmissionStatus('draft')
    }
  }

  // ── Re-open topic selection after mentor approval ───────────────────────────
  // Once a topic is approved (research_topic.status='completed'), the student is
  // locked on the completed screen. If they want a different topic, this revokes
  // the approval — research_topic back to 'draft' (clearing the approved pick)
  // and the project back to Step 1 — so they can edit their shortlist and
  // re-submit for mentor re-approval. Mirrors the direct research_topic+project
  // write already used in handleCommitDirection. (The server-side backstop in
  // evaluate-student-topic also revokes on new-topic creation, covering View-As.)
  const [reopening, setReopening] = useState(false)
  const [showReopenConfirm, setShowReopenConfirm] = useState(false)
  async function reopenTopicSelection() {
    setReopening(true)
    setError(null)
    try {
      const { error: rtErr } = await supabase
        .from('research_topic')
        .update({
          status:                     'draft',
          selected_recommendation_id: null,
          mentor_note:                null,
          updated_at:                 new Date().toISOString(),
        })
        .eq('project_id', projectId)
      if (rtErr) throw new Error(rtErr.message)
      await supabase.from('project').update({ current_step: 1 }).eq('project_id', projectId)

      setSubmissionStatus('draft')
      setFinalPickId(null)
      setMentorNote(null)
      setPickedFinalistIds([])
      // Re-opening means the student wants to CHANGE their topic, so land them on
      // the topic loop (the add/swap screen) — not the final submit screen. From
      // the loop they can drop the old topic, add a new one, and the flow carries
      // them back to the compare/submit screen once they're at 3 again.
      setPhase('loop')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setReopening(false)
    }
  }

  // ── Remove a topic from the shortlist (un-accept) ───────────────────────────
  // Lets the student drop a topic they're no longer interested in — and frees a
  // slot when they're at the 3-topic maximum and want to add a different one.
  async function removeAccepted(rec: TopicRec) {
    const { error: rmErr } = await supabase.from('topic_recommendation')
      .update({ decision: null, justification: null, decided_at: null })
      .eq('id', rec.id)
    if (rmErr) { setError(rmErr.message); return }
    setRecommendations(prev => prev.map(r =>
      r.id === rec.id ? { ...r, decision: null, justification: null } : r
    ))
    // Drop it from any finalist selection it was part of.
    setPickedFinalistIds(prev => prev.filter(id => id !== rec.id))
    if (finalPickId === rec.id) setFinalPickId(null)
  }

  // ── Generate comparison ──────────────────────────────────────────────────────
  async function runComparison() {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    setLoading(true)
    setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('compare-topics', {
        body: { project_id: projectId, student_id: studentId },
      })
      if (ctrl.signal.aborted) return
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      setComparison(data.comparison)
      setPhase('final')
    } catch (e: any) {
      if (ctrl.signal.aborted || e.name === 'AbortError') return
      setError(e.message)
    } finally {
      if (!ctrl.signal.aborted) setLoading(false)
    }
  }

  // ── Ask a question about a topic ────────────────────────────────────────────
  async function askQuestion(topic: TopicRec) {
    if (!askInput.trim()) return
    const question = askInput.trim()
    setAskInput('')
    setAskLoading(true)
    try {
      const history = askHistory[topic.id] ?? []
      const { data, error: invokeErr } = await supabase.functions.invoke('ask-about-topic', {
        body: {
          project_id:    projectId,
          student_id:    studentId,
          topic_id:      topic.id,
          topic:         { title: topic.title, description: topic.description, spanning_categories: topic.spanning_categories },
          question,
          history,
          student_grade: studentGrade,
        },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      setAskHistory(prev => ({
        ...prev,
        [topic.id]: [...(prev[topic.id] ?? []), { question, answer: data.answer }],
      }))
    } catch (e: any) {
      setAskHistory(prev => ({
        ...prev,
        [topic.id]: [...(prev[topic.id] ?? []), { question, answer: `Error: ${e.message}` }],
      }))
    } finally {
      setAskLoading(false)
    }
  }

  // ── Submit student's own topic idea ─────────────────────────────────────────
  async function submitOwnTopic() {
    if (!proposeTitle.trim()) return
    setProposeLoading(true)
    setProposeEval(null)
    try {
      // Anchor the proposed topic to the currently active category. AI evaluates
      // in that category's context and the topic's spanning_categories is just
      // the active one.
      const focusName = activeCategoryName ?? selections[0]?.category.name ?? ''
      const { data, error: invokeErr } = await supabase.functions.invoke('evaluate-student-topic', {
        body: {
          project_id:     projectId,
          student_id:     studentId,
          title:          proposeTitle.trim(),
          description:    proposeDesc.trim(),
          student_grade:  studentGrade,
          category_names: focusName ? [focusName] : selections.map(s => s.category.name),
        },
      })
      if (invokeErr) {
        // supabase-js masks non-2xx responses as a generic "non-2xx status code"
        // message; the real error lives in the response body. Surface it.
        let real = invokeErr.message
        try {
          const ctx: Response | undefined = (invokeErr as any).context
          const status = ctx?.status
          const raw = ctx ? await ctx.text() : ''
          console.error('[submitOwnTopic] edge error', { status, raw })
          let parsed: any = null
          try { parsed = JSON.parse(raw) } catch { /* not JSON */ }
          real = parsed?.error ?? (raw ? `[${status}] ${raw}` : `[${status}] ${invokeErr.message}`)
        } catch (ctxErr) {
          console.error('[submitOwnTopic] could not read edge error body', ctxErr)
        }
        throw new Error(real)
      }
      if (data?.error) throw new Error(data.error)
      setProposeEval(data.evaluation)
      setProposedRec(data.topic as TopicRec)
      // Do NOT add to recommendations yet — only add when student explicitly accepts
    } catch (e: any) {
      setError(e.message)
    } finally {
      setProposeLoading(false)
    }
  }

  // ── Submit student's own topic WITHOUT AI review ─────────────────────────────
  // Goes through evaluate-student-topic with skip_review=true: the edge function
  // skips the Claude call and inserts the topic already accepted (decision=
  // 'accept', evaluation=null). Routing through the service-role edge function
  // (instead of a direct frontend insert) is what makes this work in staff
  // View-As sessions, which have no student_profile and fail RLS on direct
  // writes to topic_recommendation.
  async function submitOwnTopicNoReview() {
    if (!proposeTitle.trim() || acceptedTopics.length >= 3) return
    setProposeLoading(true)
    setError(null)
    try {
      const focusName     = activeCategoryName ?? selections[0]?.category.name ?? ''
      const categoryNames = focusName ? [focusName] : selections.map(s => s.category.name)
      const { data, error: invokeErr } = await supabase.functions.invoke('evaluate-student-topic', {
        body: {
          project_id:     projectId,
          student_id:     studentId,
          title:          proposeTitle.trim(),
          description:    proposeDesc.trim(),
          student_grade:  studentGrade,
          category_names: categoryNames,
          skip_review:    true,
        },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      await acceptProposed(data.topic as TopicRec, true)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setProposeLoading(false)
    }
  }

  // ── Backfill AI evaluation onto a topic_recommendation that doesn't have one ──
  // Some AI-generated topics shipped without inline eval; demo-seeded topics also
  // lack one. When the student revisits these in the revision flow, they need
  // scores to make a decision.
  async function evaluateTopicOnDemand(rec: TopicRec) {
    if (evaluatingTopicId) return
    setEvaluatingTopicId(rec.id)
    setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('evaluate-topic-recommendation', {
        body: { topic_recommendation_id: rec.id },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      setRecommendations(prev => prev.map(r =>
        r.id === rec.id ? { ...r, evaluation: data.evaluation } : r
      ))
    } catch (e: any) {
      setError(e.message ?? 'Could not evaluate this topic — please try again.')
    } finally {
      setEvaluatingTopicId(null)
    }
  }

  function resetPropose() {
    setProposing(false)
    setProposeTitle('')
    setProposeDesc('')
    setProposeEval(null)
    setProposedRec(null)
  }

  function resetFinalPropose() {
    setFinalProposing(false)
    setAwaitingReplaceFor(null)
    setProposeTitle('')
    setProposeDesc('')
    setProposeEval(null)
    setProposedRec(null)
  }

  // Replace one of the 3 accepted topics with the student's own proposed topic
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function handleFinalProposeReplace(replaceTarget: TopicRec) {
    if (!awaitingReplaceFor) return
    setLoading(true)
    try {
      await Promise.all([
        // Reconsider the swapped-out topic
        supabase.from('topic_recommendation').update({
          decision: null, justification: null, decided_at: null,
        }).eq('id', replaceTarget.id),
        // Accept the proposed topic
        supabase.from('topic_recommendation').update({
          decision:      'accept',
          justification: 'My own idea',
          decided_at:    new Date().toISOString(),
        }).eq('id', awaitingReplaceFor.id),
      ])
      // Build updated recs: reconsider the swap target, and add the proposed topic as accepted
      // (proposed topic is not yet in recommendations state — append it)
      const updated: TopicRec[] = [
        ...recommendations.map(r =>
          r.id === replaceTarget.id
            ? { ...r, decision: null as null, justification: null }
            : r
        ),
        { ...awaitingReplaceFor, decision: 'accept' as const, justification: 'My own idea' },
      ]
      setRecommendations(updated)
      resetFinalPropose()
      setComparison(null)
      setPhase('comparison')
      runComparison()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // ── Load project directions ──────────────────────────────────────────────────
  async function loadDirections() {
    if (directionLoading) return
    setDirectionLoading(true)
    setDirectionError(null)
    try {
      // Return DB cache if all 4 exist
      const { data: existing } = await supabase
        .from('project_direction')
        .select('*')
        .eq('project_id', projectId)
        .order('generated_at')
      if (existing && existing.length === 4) {
        setDirections(existing as ProjectDirection[])
        return
      }
      // Fetch the confirmed topic title
      const { data: rt } = await supabase
        .from('research_topic')
        .select('title')
        .eq('project_id', projectId)
        .maybeSingle()
      const { data, error: invokeErr } = await supabase.functions.invoke('generate-project-directions', {
        body: {
          project_id:      projectId,
          student_id:      studentId,
          confirmed_topic: rt?.title ?? '',
          grade:           studentGrade,
        },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      setDirections(data.directions as ProjectDirection[])
    } catch (e: any) {
      setDirectionError(e.message)
    } finally {
      setDirectionLoading(false)
    }
  }

  // ── Commit a direction ───────────────────────────────────────────────────────
  async function handleChooseDirection(direction: ProjectDirection) {
    setCommittingDirection(true)
    setError(null)
    try {
      const track       = direction.cell.startsWith('scientific') ? 'scientific_method' as const : 'engineering_design' as const
      const projectType = direction.cell.endsWith('experimental') ? 'experimental' : 'computational'

      // 1. Stamp track + project_type on the project
      const { error: projErr } = await supabase
        .from('project')
        .update({ track, project_type: projectType })
        .eq('project_id', projectId)
      if (projErr) throw new Error(projErr.message)

      // 2. Mark research_topic as submitted
      await supabase
        .from('research_topic')
        .update({ status: 'submitted', submitted_at: new Date().toISOString() })
        .eq('project_id', projectId)

      // 3. Look up step_id for Step 1 of chosen track
      const { data: stepRow } = await supabase
        .from('step')
        .select('step_id')
        .eq('track', track)
        .eq('step_number', 1)
        .maybeSingle()

      // 4. Call step1-finalize (fire-and-forget — creates summary + journal)
      if (stepRow?.step_id) {
        supabase.functions.invoke('step1-finalize', {
          body: {
            project_id:              projectId,
            step_id:                 stepRow.step_id,
            student_id:              studentId,
            grade:                   parseInt(studentGrade ?? '9', 10),
            confirmed_track:         track,
            confirmed_project_type:  projectType,
            history: acceptedTopics.map(t => ({
              role:    'ai' as const,
              content: `Topic: ${t.title}. ${t.description ?? ''} Student reason: ${t.justification ?? ''}. Decision: ${t.decision}.`,
            })),
          },
        }).catch(() => {/* fire-and-forget */})
      }

      // 5. Log phase milestone
      supabase.from('step1_phase_log')
        .insert({ project_id: projectId, student_id: studentId, phase: 'submitted' })
        .then(() => {}, () => {})

      setPhase('submitted')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setCommittingDirection(false)
    }
  }

  // ── Ask AI about a direction ─────────────────────────────────────────────────
  async function askAboutDirection(direction: ProjectDirection) {
    if (!dirAskInput.trim()) return
    const question = dirAskInput.trim()
    setDirAskInput('')
    setDirAskLoading(true)
    try {
      const history = dirAskHistory[direction.cell] ?? []
      const { data, error: invokeErr } = await supabase.functions.invoke('ask-about-topic', {
        body: {
          project_id:    projectId,
          student_id:    studentId,
          topic: {
            title:               direction.title,
            description:         [direction.core_question, direction.what_you_do, direction.tools_materials].filter(Boolean).join(' '),
            spanning_categories: [],
          },
          question,
          history,
          student_grade: studentGrade,
        },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      setDirAskHistory(prev => ({
        ...prev,
        [direction.cell]: [...(prev[direction.cell] ?? []), { question, answer: data.answer }],
      }))
    } catch (e: any) {
      setDirAskHistory(prev => ({
        ...prev,
        [direction.cell]: [...(prev[direction.cell] ?? []), { question, answer: `Error: ${e.message}` }],
      }))
    } finally {
      setDirAskLoading(false)
    }
  }

  // ── Submit finalists for mentor review ──────────────────────────────────────
  // Replaces the old handleFinalSubmit. Multi-select is allowed (1+ topics).
  // Each picked topic carries its own justification (finalist_thoughts).
  // 1 finalist = "approve me"; 2-3 = "let's discuss". Mentor decides.
  async function handleSubmitFinalists() {
    // Only submit picks that are still accepted — guards against a stale finalist
    // (e.g. one the student reconsidered) slipping through and being rejected by
    // the "not in your accepted shortlist" server check.
    const acceptedIdSet = new Set(acceptedTopics.map(t => t.id))
    const finalists = pickedFinalistIds
      .filter(id => acceptedIdSet.has(id))
      .map(id => ({ rec_id: id, thoughts: (finalistThoughts[id] ?? '').trim() }))
    if (finalists.length < 1) {
      setError('Pick at least one of your shortlisted topics and add a short reason (30+ characters).')
      return
    }
    if (finalists.some(f => f.thoughts.length < 30)) {
      setError('Each finalist needs a reason of at least 30 characters.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step1-submit-finalists', {
        body: { project_id: projectId, finalists },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      setSubmissionStatus('submitted')
      setMentorNote(null)  // any prior round's note is now stale on the UI
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  // ── Return to category selection from any topic phase ────────────────────────
  const [showRestartConfirm, setShowRestartConfirm] = useState(false)

  // Keep student's own proposals, delete only AI-generated recs + final pick
  async function restartKeepStudentWork() {
    setLoading(true)
    try {
      await Promise.all([
        supabase.from('topic_recommendation').delete()
          .eq('project_id', projectId).eq('source', 'ai'),
        supabase.from('research_topic').delete().eq('project_id', projectId),
      ])
      setRecommendations(prev => prev.filter(r => r.source === 'student'))
      setComparison(null)
      setFinalPickId(null)
      setFinalJustify('')
      setPickedFinalistIds([])
      setSubmissionStatus(null)
      setMeetingHeldAt(null)
      setMeetingRequestedAt(null)
      setMeetingScheduledAt(null)
      setShowRestartConfirm(false)
      setPhase('category')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // Full reset — wipe everything including student proposals
  async function restartFull() {
    setLoading(true)
    try {
      await Promise.all([
        supabase.from('topic_recommendation').delete().eq('project_id', projectId),
        supabase.from('research_topic').delete().eq('project_id', projectId),
        supabase.from('category_input').delete().eq('project_id', projectId),
      ])
      setRecommendations([])
      setSelections([])
      setComparison(null)
      setFinalPickId(null)
      setFinalJustify('')
      setPickedFinalistIds([])
      setSubmissionStatus(null)
      setMeetingHeldAt(null)
      setMeetingRequestedAt(null)
      setMeetingScheduledAt(null)
      setShowRestartConfirm(false)
      setPhase('category')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // ── Toggle category selection ────────────────────────────────────────────────
  function toggleCategory(cat: CuratedCategory) {
    const exists = selections.find(s => s.category.id === cat.id)
    if (exists) {
      setSelections(prev => prev.filter(s => s.category.id !== cat.id))
    } else if (selections.length < 3) {
      setSelections(prev => [...prev, { category: cat, reason: '' }])
    }
  }

  function updateReason(categoryId: string, reason: string) {
    setSelections(prev => prev.map(s =>
      s.category.id === categoryId ? { ...s, reason } : s
    ))
  }

  // ── Derived ──────────────────────────────────────────────────────────────────
  const canSubmitCategories = selections.length >= 1 &&
    selections.every(s => s.reason.trim().length >= 30)

  // ── Render helpers ───────────────────────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const ratingColor = (r: string) =>
    r === 'low' ? 'success' : r === 'medium' ? 'warning' : 'error'

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
  }

  const isDev = process.env.REACT_APP_NODE_ENV === 'development'

  async function handleDevClearAll() {
    await Promise.all([
      supabase.from('category_input').delete().eq('project_id', projectId),
      supabase.from('topic_recommendation').delete().eq('project_id', projectId),
      supabase.from('research_topic').delete().eq('project_id', projectId),
    ])
    window.location.reload()
  }

  async function handleDevClearTopic() {
    await Promise.all([
      supabase.from('topic_recommendation').delete().eq('project_id', projectId),
      supabase.from('research_topic').delete().eq('project_id', projectId),
    ])
    window.location.reload()
  }

  // ── Category tab view ────────────────────────────────────────────────────────
  if (activeView === 'category') {
    // Gate already passed in a prior session — show read-only summary with link to continue
    if (phase !== 'category' && phase !== 'gate_failed' && phase !== 'gate_passed') {
      return (
        <Box sx={{ maxWidth: 680, py: 2 }}>
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ mb: 3 }}
            action={
              <Button size="small" color="inherit" onClick={onAdvanceToTopic} sx={{ fontWeight: 600 }}>
                Continue →
              </Button>
            }
          >
            Your category selections are confirmed.
          </Alert>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Your selected categories</Typography>
          {selections.map(sel => (
            <Card key={sel.category.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{sel.category.name}</Typography>
                {sel.category.long_description && (
                  <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                    {sel.category.long_description}
                  </Typography>
                )}
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Your reason</Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>"{sel.reason}"</Typography>
              </CardContent>
            </Card>
          ))}
          {isDev && (
            <Box sx={{ mt: 4, pt: 2, borderTop: '1px dashed', borderColor: 'error.light' }}>
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>DEV ONLY</Typography>
              <Button variant="outlined" color="error" size="small" onClick={handleDevClearAll}>
                Clear Everything &amp; Start Over
              </Button>
            </Box>
          )}
        </Box>
      )
    }
    // Gate not yet passed (or gate_passed review in progress) — fall through
  }

  // ── Topic tab: redirect to category tab if gate not passed ───────────────────
  if (activeView === 'topic') {
    if (phase === 'category' || phase === 'gate_failed' || phase === 'gate_passed') {
      return (
        <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Complete the <strong>Research Category</strong> tab first.
          </Typography>
          <Typography variant="body2">
            Select at least 1 category and write your reason to unlock this tab.
          </Typography>
        </Box>
      )
    }
    // Gate passed — render topic phases below (loop/comparison/final/submitted/completed)
  }

  // ── AI Category Review (gate just passed) ────────────────────────────────────
  if (phase === 'gate_passed') {
    const SCORE_DIMS = [
      { key: 'specificity', label: 'Specificity',        max: 4, tip: 'Does it name something real and precise?' },
      { key: 'connection',  label: 'Personal Connection', max: 3, tip: 'Is it anchored to your own life?' },
      { key: 'effort',      label: 'Personal angle',      max: 3, tip: 'Did you bring your own story?' },
    ]
    const scoreColor = (v: number, max: number) => v === max ? '#2e7d32' : v >= max - 1 ? '#ed6c02' : '#c62828'
    const scoreBg    = (v: number, max: number) => v === max ? 'rgba(46,125,50,0.08)' : v >= max - 1 ? 'rgba(237,108,2,0.08)' : 'rgba(198,40,40,0.08)'
    const hasPrev    = prevGateScores.length === gateScores.length && prevGateScores.length > 0

    return (
      <Box sx={{ maxWidth: 680, py: 2 }}>
        {/* Header */}
        <Box sx={{
          mb: 3, p: 2.5,
          bgcolor: 'rgba(46,125,50,0.06)', border: '1px solid rgba(46,125,50,0.25)', borderRadius: 2,
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28, flexShrink: 0 }} />
          <Box>
            <Typography sx={{ fontWeight: 700, color: 'success.dark' }}>
              {selections.length === 1 ? 'Your story came through' : 'Your stories came through'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Here's how the AI scored {selections.length === 1 ? 'your reason' : 'each of your reasons'}. Read through, then decide whether to continue or strengthen {selections.length === 1 ? 'it' : 'them'}.
            </Typography>
          </Box>
        </Box>

        {/* Variability note — shown after second check-in so the student has seen it */}
        {hasPrev && (
          <Box sx={{ mb: 2.5, px: 2, py: 1.5, bgcolor: 'rgba(103,58,183,0.05)', border: '1px solid rgba(103,58,183,0.15)', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#4527a0', display: 'block', mb: 0.5 }}>
              Why did my scores change?
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.7, display: 'block' }}>
              AI scoring, like feedback from any expert, involves judgment — and judgment varies slightly from one reading to the next.
              The same text can land differently depending on the weight the model places on particular phrases.
              Real expert reviewers — science fair judges, professors, grant committees — experience this too.
              What matters is the <strong>pattern across all dimensions</strong>, not any single number.
              If you see shifts, look at which dimensions moved and ask yourself whether that reflects something real in your writing.
            </Typography>
          </Box>
        )}

        {/* Scores per category */}
        {gateScores.length > 0 ? (
          gateScores.map((sc, i) => (
            <Card key={i} variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{sc.name}</Typography>
                {/* Reason text from selections */}
                {selections[i]?.reason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic', lineHeight: 1.6 }}>
                    "{selections[i].reason}"
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {SCORE_DIMS.map(({ key, label, max }) => {
                    const val     = (sc as any)[key] as number
                    const prevVal = hasPrev ? (prevGateScores[i] as any)?.[key] as number : null
                    const delta   = prevVal != null ? val - prevVal : null
                    return (
                      <Box
                        key={key}
                        sx={{
                          px: 1.5, py: 0.75, borderRadius: 1.5,
                          bgcolor: scoreBg(val, max),
                          border: `1px solid ${scoreColor(val, max)}40`,
                          minWidth: 110,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600, color: scoreColor(val, max), display: 'block' }}>
                          {label}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          {Array.from({ length: max }, (_, i) => i + 1).map(n => (
                            <Box
                              key={n}
                              sx={{
                                width: 10, height: 10, borderRadius: '50%',
                                bgcolor: n <= val ? scoreColor(val, max) : '#e0e0e0',
                              }}
                            />
                          ))}
                          <Typography variant="caption" sx={{ ml: 0.5, color: scoreColor(val, max), fontWeight: 700 }}>
                            {val}/{max}
                          </Typography>
                          {delta !== null && delta !== 0 && (
                            <Typography variant="caption" sx={{
                              ml: 0.5, fontWeight: 700,
                              color: delta > 0 ? '#2e7d32' : '#c62828',
                            }}>
                              {delta > 0 ? `+${delta}` : `${delta}`}
                            </Typography>
                          )}
                          {delta === 0 && (
                            <Typography variant="caption" sx={{ ml: 0.5, color: 'text.disabled' }}>
                              =
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )
                  })}
                  <Box sx={{
                    px: 1.5, py: 0.75, borderRadius: 1.5,
                    bgcolor: 'rgba(25,118,210,0.06)',
                    border: '1px solid rgba(25,118,210,0.2)',
                    minWidth: 70,
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', display: 'block' }}>Total</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main', mt: 0.25 }}>
                        {sc.total} / 10
                      </Typography>
                      {hasPrev && (() => {
                        const totalDelta = sc.total - (prevGateScores[i]?.total ?? sc.total)
                        return totalDelta !== 0 ? (
                          <Typography variant="caption" sx={{ fontWeight: 700, color: totalDelta > 0 ? '#2e7d32' : '#c62828', mt: 0.25 }}>
                            {totalDelta > 0 ? `+${totalDelta}` : `${totalDelta}`}
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.25 }}>＝</Typography>
                        )
                      })()}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        ) : (
          // Scores not available (page reload) — show selections only
          selections.map(sel => (
            <Card key={sel.category.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{sel.category.name}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>"{sel.reason}"</Typography>
              </CardContent>
            </Card>
          ))
        )}

        {/* AI coaching message */}
        {gateScores.length > 0 && (() => {
          const hasTwos = gateScores.some(s => s.specificity < 3 || s.connection < 3 || s.effort < 3)
          return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 3, p: 2, bgcolor: 'rgba(25,118,210,0.03)', border: '1px solid', borderColor: 'primary.100', borderRadius: 2 }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                bgcolor: 'primary.main', color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.6rem', fontWeight: 800,
              }}>AI</Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {hasTwos
                  ? <>Specificity is scored out of 4 — the highest score goes to reasons that cite something precise: a named event, a specific person, exact numbers, or a particular place. Personal Connection and Personal Angle are out of 3. The more your own story shows up, the better I can match topics to what you actually care about. <strong>You choose whether to strengthen {gateScores.length === 1 ? 'it' : 'them'} or move on.</strong></>
                  : gateScores.length === 1
                    ? <>Your reason scored well. The category is grounded in real interest — that's exactly what makes for good research.</>
                    : <>{gateScores.length === 2 ? 'Both' : 'All three'} reasons scored well. Your categories are grounded in real interest — that's exactly what makes for good research.</>
                }
              </Typography>
            </Box>
          )
        })()}

        {/* Decision */}
        {isTrial ? (
          <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.025)', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
              This is the end of your trial.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              You've done real work here — you selected research categories, wrote genuine reasons for each one,
              and passed the AI review. That reflection belongs in your research notebook.
              Print your journal to keep a record of everything you did in this session.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Button variant="contained" size="large" startIcon={<PrintIcon />} onClick={onAdvanceToTopic} sx={{ px: 3 }}>
                Save to Journal
              </Button>
              <Button variant="outlined" size="large" onClick={() => setPhase('category')} sx={{ px: 3 }}>
                Revise stories or categories
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}>
            <Card
              variant="outlined"
              sx={{ cursor: 'pointer', '&:hover': { borderColor: 'warning.main', bgcolor: 'rgba(237,108,2,0.03)' }, transition: 'all 0.15s' }}
              onClick={() => setPhase('category')}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Revise my stories or categories
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Add more detail to your stories — <em>or</em> swap a category if you realize it doesn't
                  have a real story behind it. Either is fine, and it leads to better topic recommendations.
                </Typography>
              </CardContent>
            </Card>
            <Card
              variant="outlined"
              sx={{ cursor: 'pointer', '&:hover': { borderColor: 'success.main', bgcolor: 'rgba(46,125,50,0.03)' }, transition: 'all 0.15s' }}
              onClick={() => {
                setPhase('loop')
                if (!recommendations.length) fetchRecommendations()
                onAdvanceToTopic()
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  I'm satisfied — explore topics
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Your stories are good enough to move forward. Start exploring research topics based on your categories.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    )
  }

  // ── Restart confirmation panel ────────────────────────────────────────────────
  const studentProposalCount = recommendations.filter(r => r.source === 'student').length

  const restartConfirmPanel = showRestartConfirm && (
    <Box sx={{
      mt: 3, p: 2.5, borderRadius: 2,
      border: '1px solid', borderColor: 'warning.main',
      bgcolor: 'rgba(237,108,2,0.04)',
    }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Change your research categories?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
        AI-suggested topics will be removed because they were matched to your current categories.
        {studentProposalCount > 0
          ? ` Your ${studentProposalCount} own topic idea${studentProposalCount > 1 ? 's' : ''} will be kept.`
          : ''}
        {' '}You can also start completely fresh and clear everything, including your category selections.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <Button
          variant="contained"
          color="warning"
          size="small"
          onClick={restartKeepStudentWork}
          disabled={loading}
        >
          {loading ? <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} /> : null}
          Change categories{studentProposalCount > 0 ? ', keep my ideas' : ''}
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={restartFull}
          disabled={loading}
        >
          Start completely fresh
        </Button>
        <Button
          variant="text"
          size="small"
          sx={{ color: 'text.secondary' }}
          onClick={() => setShowRestartConfirm(false)}
          disabled={loading}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  )

  // ── Completed ────────────────────────────────────────────────────────────────

  const devClearButton = isDev && (
    <Box sx={{ mt: 4, pt: 2, borderTop: '1px dashed', borderColor: 'error.light' }}>
      <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>DEV ONLY</Typography>
      <Button variant="outlined" color="error" size="small" onClick={handleDevClearTopic}>
        Clear Topic Data &amp; Start Over
      </Button>
    </Box>
  )

  // ── Direction discovery ───────────────────────────────────────────────────────
  if (phase === 'direction') {
    const CELL_META: Record<string, { trackLabel: string; methodLabel: string; trackColor: string; methodColor: string }> = {
      scientific_experimental:  { trackLabel: 'Scientific Inquiry',  methodLabel: 'Experimental',  trackColor: '#1565c0', methodColor: '#2e7d32' },
      scientific_computational: { trackLabel: 'Scientific Inquiry',  methodLabel: 'Computational',  trackColor: '#1565c0', methodColor: '#6a1b9a' },
      engineering_experimental: { trackLabel: 'Engineering Design',  methodLabel: 'Experimental',  trackColor: '#e65100', methodColor: '#2e7d32' },
      engineering_computational:{ trackLabel: 'Engineering Design',  methodLabel: 'Computational', trackColor: '#e65100', methodColor: '#6a1b9a' },
    }

    // Order: sci-exp, sci-comp, eng-exp, eng-comp
    const CELL_ORDER = ['scientific_experimental','scientific_computational','engineering_experimental','engineering_computational']
    const sorted = CELL_ORDER.map(c => directions.find(d => d.cell === c)).filter(Boolean) as ProjectDirection[]

    const topicTitle = acceptedTopics.find(t => t.id === finalPickId)?.title ?? null

    return (
      <Box sx={{ maxWidth: 820, py: 2 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Discover Your Project Direction
          </Typography>
          {topicTitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Topic: <strong>{topicTitle}</strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            Every research topic can be approached multiple ways. Here are four directions your
            project could take — two tracks, each with an experimental and a computational approach.
            Expand any card to see exactly what the project would look like before you commit.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Loading state */}
        {directionLoading && directions.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Generating your personalized directions...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Mapping your topic across all four approaches.
            </Typography>
          </Box>
        )}

        {directionError && (
          <Alert severity="error" sx={{ mb: 2 }} action={
            <Button color="inherit" size="small" onClick={() => { setDirectionError(null); loadDirections() }}>
              Retry
            </Button>
          }>{directionError}</Alert>
        )}

        {/* 2×2 grid */}
        {sorted.length === 4 && (
          <>
            {/* Column headers */}
            <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '1fr 1fr', gap: 2, mb: 1, pl: 0 }}>
              {['Experimental', 'Computational'].map(label => (
                <Box key={label} sx={{ textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.1em' }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              {sorted.map(dir => {
                const meta      = CELL_META[dir.cell]
                const isExpanded = expandedCell === dir.cell
                const history   = dirAskHistory[dir.cell] ?? []
                return (
                  <Card
                    key={dir.cell}
                    variant="outlined"
                    sx={{
                      display:      'flex',
                      flexDirection:'column',
                      border:        isExpanded ? '2px solid' : '1px solid',
                      borderColor:   isExpanded ? 'primary.main' : 'divider',
                      transition:   'border-color 0.15s',
                    }}
                  >
                    <CardContent sx={{ flex: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      {/* Track + Method badges */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                        <Chip
                          label={meta.trackLabel}
                          size="small"
                          sx={{ bgcolor: meta.trackColor, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={meta.methodLabel}
                          size="small"
                          sx={{ bgcolor: meta.methodColor, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      </Box>

                      {/* Title */}
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.4, mb: 1 }}>
                        {dir.title}
                      </Typography>

                      {/* Summary */}
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: 1.5 }}>
                        {dir.summary}
                      </Typography>

                      {/* Expand toggle */}
                      <Button
                        size="small"
                        variant="text"
                        endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        onClick={() => setExpandedCell(isExpanded ? null : dir.cell)}
                        sx={{ fontSize: '0.8rem', color: 'text.secondary', p: 0, mb: isExpanded ? 2 : 0 }}
                      >
                        {isExpanded ? 'Hide details' : 'Explore this direction'}
                      </Button>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <Box>
                          <Divider sx={{ mb: 2 }} />

                          {[
                            { label: 'Core question',        value: dir.core_question    },
                            { label: 'What you would do',    value: dir.what_you_do      },
                            { label: 'Tools & materials',    value: dir.tools_materials  },
                            { label: 'Your data / output',   value: dir.data_format      },
                            { label: 'Finished project',     value: dir.finished_project },
                          ].filter(item => item.value).map(item => (
                            <Box key={item.label} sx={{ mb: 1.75 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                {item.label}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.4, lineHeight: 1.65 }}>
                                {item.value}
                              </Typography>
                            </Box>
                          ))}

                          {/* Inline Q&A */}
                          <Divider sx={{ my: 1.5 }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}>
                            Ask AI about this direction
                          </Typography>

                          {history.map((qa, i) => (
                            <Box key={i} sx={{ mb: 1.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                Q: {qa.question}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
                                <Box sx={{
                                  width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                  bgcolor: 'primary.main', color: 'white',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '0.6rem', fontWeight: 800,
                                }}>AI</Box>
                                <Box sx={{ bgcolor: 'rgba(25,118,210,0.05)', border: '1px solid', borderColor: 'primary.light', borderRadius: 1.5, p: 1.25, flex: 1 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                    {qa.answer}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))}

                          {dirAskCell === dir.cell ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                multiline
                                maxRows={3}
                                placeholder="What would you like to know about this approach?"
                                value={dirAskInput}
                                onChange={e => setDirAskInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askAboutDirection(dir) } }}
                                disabled={dirAskLoading}
                                autoFocus
                              />
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => askAboutDirection(dir)}
                                disabled={!dirAskInput.trim() || dirAskLoading}
                                sx={{ flexShrink: 0, mt: 0.25 }}
                              >
                                {dirAskLoading && dirAskCell === dir.cell
                                  ? <CircularProgress size={16} color="inherit" />
                                  : 'Ask'}
                              </Button>
                            </Box>
                          ) : (
                            <Button
                              size="small"
                              variant="text"
                              sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5, p: 0 }}
                              onClick={() => { setDirAskCell(dir.cell); setDirAskInput('') }}
                            >
                              {history.length > 0 ? '+ Ask another question' : 'Have a question about this approach?'}
                            </Button>
                          )}

                          {/* Choose button */}
                          <Box sx={{ mt: 1 }}>
                            <Button
                              variant="contained"
                              size="large"
                              fullWidth
                              onClick={() => handleChooseDirection(dir)}
                              disabled={committingDirection}
                              sx={{ fontWeight: 700 }}
                            >
                              {committingDirection
                                ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />Saving...</>
                                : `Choose ${meta.trackLabel} — ${meta.methodLabel} →`}
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )
              })}
            </Box>
          </>
        )}

        {devClearButton}
      </Box>
    )
  }

  // ── Submitted ────────────────────────────────────────────────────────────────
  if (phase === 'submitted') {
    return (
      <Box sx={{ py: 4 }}>
        {/* Achievement moment */}
        <Box sx={{
          mb:           3,
          p:            3,
          bgcolor:      'primary.main',
          borderRadius: 3,
          color:        '#fff',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Research direction submitted!
            </Typography>
          </Box>
          <Typography sx={{ opacity: 0.85, fontSize: '0.9rem', lineHeight: 1.6 }}>
            Your mentor will review your topic, your reasoning, and your chosen project direction.
            Expect feedback and a conversation before you move to Step 2.
          </Typography>
        </Box>

        {finalPickId && acceptedTopics.find(t => t.id === finalPickId) && (
          <Card variant="outlined" sx={{ mt: 1, p: 2 }}>
            <Typography variant="overline" color="text.secondary">Your confirmed topic</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {acceptedTopics.find(t => t.id === finalPickId)?.title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>{finalJustify}</Typography>
          </Card>
        )}
        {devClearButton}
      </Box>
    )
  }

  // ── Completed ────────────────────────────────────────────────────────────────
  if (phase === 'completed') {
    const approved = finalPickId ? acceptedTopics.find(t => t.id === finalPickId) : null

    async function startStep2() {
      setAdvancing(true)
      try {
        const { error: upErr } = await supabase
          .from('project')
          .update({ current_step: 2 })
          .eq('project_id', projectId)
          .lt('current_step', 2)  // never regress
        if (upErr) { setError(upErr.message); setAdvancing(false); return }
        navigate(prefixPath('/step/2'))
      } catch (e: any) {
        setError(e.message ?? 'Could not advance to Step 2.')
        setAdvancing(false)
      }
    }

    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
          Your mentor approved your research topic. Step 1 is complete!
        </Alert>

        {approved && (
          <Card variant="outlined" sx={{ mb: 3, p: 2.5 }}>
            <Typography variant="overline" color="text.secondary">Approved topic</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {approved.title}
            </Typography>
            {approved.description && (
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                {approved.description}
              </Typography>
            )}
          </Card>
        )}

        {/* Changed your mind? — re-open topic selection. Revokes the approval and
            sends the project back to Step 1, so it's gated behind a confirm. */}
        {step2Approved ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Want a different topic? Your Step 2 is already approved, so ask your mentor from
            the Mentor Session tab — they'll reopen Step 1 for you.
          </Typography>
        ) : !showReopenConfirm ? (
          <Box sx={{ mb: 3 }}>
            <Button size="small" variant="text" color="inherit" sx={{ color: 'text.secondary' }}
              onClick={() => setShowReopenConfirm(true)} disabled={reopening}>
              Want a different topic? Re-open topic selection
            </Button>
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mb: 3 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" color="inherit" onClick={() => setShowReopenConfirm(false)} disabled={reopening}>
                  Keep it
                </Button>
                <Button size="small" color="warning" variant="contained" onClick={reopenTopicSelection} disabled={reopening}
                  endIcon={reopening ? <CircularProgress size={14} color="inherit" /> : undefined}>
                  Re-open
                </Button>
              </Box>
            }>
            Re-opening sends this project back to Step 1. Your mentor's approval is
            revoked and you'll need to re-submit for approval. Any Step 2 work you've
            started stays in place for you to revise.
          </Alert>
        )}

        {mentorNote && (
          <Card variant="outlined" sx={{ mb: 3, p: 2.5, borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.03)' }}>
            <Typography variant="overline" color="primary.main">Mentor's note</Typography>
            <Markdown>{mentorNote}</Markdown>
          </Card>
        )}

        {/* Reflection capture — student writes what they learned at Step 1 close */}
        <StepReflectionCard
          projectId={projectId}
          stepNumber={1}
          stepLabel="Step 1: Topic Selection"
          onSavedChange={setReflectionDone}
        />

        {/* Step 2 launch — gated on reflection */}
        <Box sx={{ mt: 3, p: 2.5, borderRadius: 2, border: '1px solid', borderColor: reflectionDone ? 'primary.main' : 'divider', bgcolor: reflectionDone ? 'rgba(15,43,76,0.03)' : 'background.paper' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Ready for Step 2: Ask a Research Question
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
            Next, you'll narrow your topic into a specific, testable research question and identify
            the variables you'll measure.
          </Typography>
          <Button
            variant="contained"
            onClick={startStep2}
            disabled={!reflectionDone || advancing}
            endIcon={advancing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {reflectionDone ? 'Start Step 2 →' : 'Save your reflection above first'}
          </Button>
        </Box>

        {devClearButton}
      </Box>
    )
  }

  // ── Category selection ───────────────────────────────────────────────────────
  if (phase === 'category' || phase === 'gate_failed') {
    const storyCount = selections.length
    const storyWord  = storyCount === 1 ? 'story' : 'stories'
    return (
      <Box sx={{ maxWidth: 700, py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          Choose Your Research Categories
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Browse the categories below and pick up to <strong>3</strong> that genuinely make you curious —
          not what sounds impressive, but what you actually care about.
        </Typography>

        {/* Why this matters — judges + AI coach */}
        <Box sx={{
          mb: 3, p: 2.5, borderRadius: 2,
          bgcolor: 'rgba(25,118,210,0.05)',
          border: '1px solid', borderColor: 'rgba(25,118,210,0.25)',
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
            Tell your story — judges will ask you to.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
            When you stand in front of a science fair judge, they almost always open with:{' '}
            <em>"Why did you pick this?"</em> The strongest answers are personal and specific — a moment, a
            person, a problem that stuck with you. Your story here also shapes every topic suggestion your
            AI Coach makes next, so being honest and specific pays off twice — once in your project, once
            on judging day.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!error && categories.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No categories loaded — please refresh. If this persists, contact support.
          </Alert>
        )}

        {/* Progress */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={(selections.length / 3) * 100}
            sx={{ flex: 1, height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {selections.length} / 3 selected (1 minimum)
          </Typography>
        </Box>

        {/* Category browser — always visible so the student can swap a category that doesn't have a real story behind it */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4 }}>
          {categories.map(cat => {
            const isSelected = selections.some(s => s.category.id === cat.id)
            const isExpanded = expanded === cat.id
            const disabled   = !isSelected && selections.length >= 3
            return (
              <Card
                key={cat.id}
                variant="outlined"
                sx={{
                  cursor:      disabled ? 'not-allowed' : 'pointer',
                  opacity:     disabled ? 0.5 : 1,
                  border:      isSelected ? '2px solid' : '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  transition:  'border-color 0.15s',
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1 }} onClick={() => !disabled && toggleCategory(cat)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isSelected && <CheckCircleIcon color="primary" sx={{ fontSize: 18 }} />}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{cat.name}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                        {cat.short_description}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={e => { e.stopPropagation(); setExpanded(isExpanded ? null : cat.id) }}
                    >
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>

                  {isExpanded && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      {cat.long_description && (
                        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                          {cat.long_description}
                        </Typography>
                      )}
                      {cat.example_projects && (
                        <>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            Example projects:
                          </Typography>
                          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                            {cat.example_projects.map((ex, i) => (
                              <Typography key={i} component="li" variant="caption" color="text.secondary">
                                {ex}
                              </Typography>
                            ))}
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </Box>

        {/* Reason inputs for selected categories */}
        {selections.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {phase === 'gate_failed'
                ? `Tell us a bit more of your ${storyWord}`
                : `Tell your ${storyWord}`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              For each category, share a specific moment, person, or experience that sparked your
              curiosity — and what you'd want to figure out. Skip the generic; we want your version of why.
            </Typography>

            {/* Collapsible strong example */}
            <Box sx={{ mb: 3 }}>
              <Box
                onClick={() => setExampleOpen(o => !o)}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  cursor: 'pointer', color: 'primary.main', fontSize: '0.85rem', fontWeight: 600,
                }}
              >
                {exampleOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                What does a strong reason look like?
              </Box>
              {exampleOpen && (
                <Box sx={{ mt: 1.5, p: 2, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                    Example category: Animal Sciences
                  </Typography>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#c62828' }}>
                      Less strong:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.25 }}>
                      "I like animals and want to learn how to train dogs better."
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      Strong:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.25, lineHeight: 1.6 }}>
                      "When I was 8, my dog started having seizures and our vet couldn't tell us why.
                      That stuck with me. I want to understand how the brain processes stress signals —
                      maybe even build something that could warn families before a seizure starts."
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Why the second one is strong:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <Typography component="li" variant="caption" color="text.secondary">
                      A specific moment ("when I was 8…")
                    </Typography>
                    <Typography component="li" variant="caption" color="text.secondary">
                      A real question it raised ("how the brain processes stress signals")
                    </Typography>
                    <Typography component="li" variant="caption" color="text.secondary">
                      A hint at where it could go ("warn families before a seizure starts")
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {selections.map(sel => (
              <Box key={sel.category.id} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {sel.category.name}
                </Typography>
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="A specific moment, person, or experience that sparked this — and what you'd want to figure out..."
                  value={sel.reason}
                  onChange={e => updateReason(sel.category.id, e.target.value)}
                  helperText={
                    sel.reason.trim().length < 30
                      ? `${sel.reason.trim().length} / 30 characters minimum — be specific`
                      : 'Good — your AI coach will use this story to personalize what comes next'
                  }
                  error={sel.reason.trim().length > 0 && sel.reason.trim().length < 30}
                />
              </Box>
            ))}
          </Box>
        )}

        {phase === 'gate_failed' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>We need a bit more of your {storyWord}.</strong>
            {gateFeedback ? (
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                {gateFeedback.split('\n').filter(Boolean).map((line, i) => <li key={i}>{line}</li>)}
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                Your {storyWord === 'story' ? 'reason is' : 'reasons are'} a start, but a judge — and your AI Coach — would want more of <em>you</em> coming through. Try adding one of these to each:
                <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                  <li>A specific moment when you first noticed this</li>
                  <li>A person in your life connected to it</li>
                  <li>A question you've been quietly wondering about it</li>
                </Box>
              </Box>
            )}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleGateSubmit}
            disabled={loading || !canSubmitCategories}
            title="Your AI Coach reads what you wrote and tells you whether your story is coming through."
            sx={{ px: 5 }}
          >
            {loading
              ? <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Reviewing...</>
              : `Review my ${storyWord} →`
            }
          </Button>
        </Box>
      </Box>
    )
  }

  // ── Topic recommendation loop ────────────────────────────────────────────────
  if (phase === 'loop') {
    const studentAuthoredCount = acceptedTopics.filter(t => t.source === 'student').length
    const activeCategory = selections.find(s => s.category.id === (activeCategoryId || selections[0]?.category.id))
    return (
      <Box sx={{ maxWidth: 680, py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          Pick Your Research Topics
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick <strong>3 topics</strong> to compare — one in each of your selected categories.{' '}
          <strong>At least one in your own words.</strong> Already have a topic of your own? Add it
          and continue to submit — no need to pick more. You'll turn your topic into a specific
          research question in Step 2.
        </Typography>

        {/* Project frame — fixed time expectation */}
        <Box sx={{
          mb: 3, p: 2.5, borderRadius: 2,
          bgcolor: 'rgba(25,118,210,0.05)',
          border: '1px solid', borderColor: 'rgba(25,118,210,0.25)',
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
            A real science fair project is ~100–150 hours of work.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
            That's roughly 10–15 weeks of evening + weekend time. Pick a direction you'd want to spend
            that time on — and one you can actually finish. Your AI Coach will flag obvious blockers
            (gear you probably can't get, costs that are unrealistic at home, skills you'd need to
            build first), but you make the call. You know your situation; the AI doesn't.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Progress */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={(acceptedTopics.length / 3) * 100}
            color="success"
            sx={{ flex: 1, height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {acceptedTopics.length} / 3 topics
          </Typography>
        </Box>
        <Typography variant="caption" color={studentAuthoredCount > 0 ? 'success.main' : 'text.secondary'} sx={{ display: 'block', mb: 3 }}>
          {studentAuthoredCount > 0
            ? `${studentAuthoredCount} in your own words ✓`
            : 'At least one must be in your own words'}
        </Typography>

        {/* Accepted chips — deletable so students can drop a topic they no
            longer want (and free a slot at the 3-topic maximum). */}
        {acceptedTopics.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {acceptedTopics.map(t => (
                <Chip
                  key={t.id}
                  label={t.source === 'student' ? `✏️ ${t.title}` : t.title}
                  color="success" size="small"
                  icon={t.source === 'student' ? undefined : <CheckCircleIcon />}
                  onDelete={() => removeAccepted(t)}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              Tap the ✕ on a topic to remove it from your shortlist.
            </Typography>
          </Box>
        )}

        {/* Way forward to the submit screen. Reaching 3 auto-advances, but a
            student with a topic of their own may go now — no need to fill the
            shortlist with topics they don't want — and one who re-opened topic
            selection lands here already at 3. */}
        {(acceptedTopics.length >= 3 || studentAuthoredCount >= 1) && (
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              onClick={() => {
                if (studentAuthoredCount === 0) { setNeedStudentAuthored(true); return }
                setSubmissionStatus('draft')
                setPhase('final')
              }}
            >
              {acceptedTopics.length >= 2 ? <>Continue to compare &amp; submit →</> : 'Continue to submit my topic →'}
            </Button>
          </Box>
        )}

        {needStudentAuthored && (
          <Alert severity="info" sx={{ mb: 3 }} onClose={() => setNeedStudentAuthored(false)}>
            <strong>Add at least one topic in your own words before continuing.</strong>{' '}
            Use the <em>Propose your own topic</em> card below — even a half-formed idea is fine.
          </Alert>
        )}

        {/* ── CATEGORY TABS — student's selected categories ───────────── */}
        {selections.length > 0 && (
          <Tabs
            value={activeCategoryId || selections[0].category.id}
            onChange={(_, v) => {
              setActiveCategoryId(v)
              setExamplesOpen(false)
              resetPropose()
              setWonderPrompts(null)
              setError(null)
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            {selections.map(s => {
              const accepted = acceptedTopics.some(t => t.spanning_categories?.includes(s.category.name))
              return (
                <Tab
                  key={s.category.id}
                  value={s.category.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {accepted && <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />}
                      <span>{s.category.name}</span>
                    </Box>
                  }
                />
              )
            })}
          </Tabs>
        )}

        {/* ── ACTIVE CATEGORY: reason + example projects ──────────────── */}
        {activeCategory && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ p: 2, mb: 1.5, borderRadius: 1.5, bgcolor: 'rgba(0,0,0,0.025)', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                Why you picked this category
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>"{activeCategory.reason}"</Typography>
            </Box>

            {(activeCategory.category.example_projects?.length ?? 0) > 0 && (
              <Box>
                <Box
                  onClick={() => setExamplesOpen(o => !o)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    cursor: 'pointer', color: 'primary.main', fontSize: '0.85rem', fontWeight: 600,
                  }}
                >
                  {examplesOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  Example projects in {activeCategory.category.name}
                </Box>
                {examplesOpen && (
                  <Box sx={{ mt: 1.5, p: 2, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                      For inspiration only — your topic should be your own. Click one to seed your title, then make it yours.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {activeCategory.category.example_projects!.map((p, i) => (
                        <Box
                          key={i}
                          onClick={() => { setProposeTitle(p); setExamplesOpen(false) }}
                          sx={{
                            p: 1.25, borderRadius: 1, cursor: 'pointer',
                            bgcolor: '#fff', border: '1px solid', borderColor: 'divider',
                            '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(25,118,210,0.04)' },
                          }}
                        >
                          <Typography variant="body2">{p}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* ── PROPOSE YOUR OWN TOPIC (primary) ────────────────────────── */}
        <Card variant="outlined" sx={{ mb: 3, borderColor: 'primary.light', borderWidth: 2 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Propose your topic{activeCategory ? ` in ${activeCategory.category.name}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              A topic is a focused area of investigation in this category — not yet a research question
              (that's Step 2). Even a half-formed idea is fine. Your AI Coach will give an honest read;
              you make the call.
            </Typography>

            {!proposeEval ? (
              <>
                <TextField
                  fullWidth size="small"
                  label="Topic title"
                  placeholder="e.g. How nutrition affects honeybee colony health"
                  value={proposeTitle}
                  onChange={e => setProposeTitle(e.target.value)}
                  sx={{ mb: 1.5 }}
                  required
                />
                <TextField
                  fullWidth size="small" multiline rows={5}
                  label="Tell us about the project you imagine"
                  placeholder="What would you do? What would you measure or build? What materials or tools? What part interests you most?"
                  value={proposeDesc}
                  onChange={e => setProposeDesc(e.target.value)}
                  helperText={
                    proposeDesc.trim().length < 100
                      ? `${proposeDesc.trim().length} / 100 minimum — give us a real picture. The more concrete you are, the more useful the AI's feedback gets — and you'll already have a head start on Step 2.`
                      : 'Good — your AI Coach will anchor feedback in what you wrote.'
                  }
                  error={proposeDesc.trim().length > 0 && proposeDesc.trim().length < 100}
                  sx={{ mb: 2 }}
                  required
                />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained" size="small"
                    onClick={submitOwnTopic}
                    disabled={!proposeTitle.trim() || proposeDesc.trim().length < 100 || proposeLoading}
                  >
                    {proposeLoading
                      ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Reviewing...</>
                      : 'Get feedback'}
                  </Button>
                  <Button
                    variant="outlined" size="small"
                    onClick={submitOwnTopicNoReview}
                    disabled={!proposeTitle.trim() || proposeLoading || acceptedTopics.length >= 3}
                  >
                    Add without AI review
                  </Button>
                  {!wonderPrompts && (
                    <Button
                      variant="text" size="small"
                      sx={{ color: 'text.secondary' }}
                      disabled={wonderLoading}
                      onClick={fetchWonderPrompts}
                    >
                      {wonderLoading
                        ? <><CircularProgress size={14} sx={{ mr: 1 }} />Thinking...</>
                        : "I'm stuck — give me ideas to wonder about"}
                    </Button>
                  )}
                </Box>

                {wonderPrompts && wonderPrompts.length > 0 && (
                  <Box sx={{ mt: 2.5, p: 2, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Things you might wonder about{activeCategory ? ` in ${activeCategory.category.name}` : ''}
                      </Typography>
                      <Button
                        size="small" variant="text" sx={{ fontSize: '0.7rem', color: 'text.secondary', minWidth: 0, p: 0.5 }}
                        onClick={() => setWonderPrompts(null)}
                      >
                        Hide
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                      These are starting points to think with — not your research question.
                      Click one to seed your topic title, then make it your own.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {wonderPrompts.map((p, i) => (
                        <Box
                          key={i}
                          onClick={() => { setProposeTitle(p); setWonderPrompts(null) }}
                          sx={{
                            p: 1.25, borderRadius: 1, cursor: 'pointer',
                            bgcolor: '#fff', border: '1px solid', borderColor: 'divider',
                            '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(25,118,210,0.04)' },
                          }}
                        >
                          <Typography variant="body2">{p}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              // ── Evaluation result ──
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>{proposeTitle}</Typography>
                {proposeDesc && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{proposeDesc}</Typography>
                )}

                {proposeEval.unavailable && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    The automated review wasn't available for this topic, so no scores are shown.
                    You can still add it to your shortlist and refine it in Step 2.
                  </Alert>
                )}

                {/* 5-dim feasibility */}
                <Box sx={{ mb: 2 }}>
                  {(['time_fit', 'budget', 'equipment', 'skills', 'specificity'] as const).map(key => {
                    const d = proposeEval[key]
                    if (!d) return null
                    return <DimRow key={key} dimKey={key} d={d} />
                  })}
                </Box>

                {proposeEval.step2_hint && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mb: 1.5 }}>
                    {proposeEval.step2_hint}
                  </Typography>
                )}

                {(proposeEval.research_questions ?? []).length > 0 && (
                  <ResearchQuestions questions={proposeEval.research_questions} />
                )}

                {/* Disclaimer */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.72rem', opacity: 0.85 }}>
                  ⓘ {proposeEval.ai_disclaimer ?? "The AI may misjudge time, cost, equipment, or skill — it's a starting point, not the verdict. You know your situation best."}
                </Typography>

                {/* Save this evaluation to journal */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <SaveToJournalButton
                    projectId={projectId}
                    stepNumber={1}
                    sourceLabel={`AI Topic Evaluation — ${proposeTitle}`}
                    content={(() => {
                      const parts: string[] = [`Topic: ${proposeTitle}`, '', `Description: ${proposeDesc}`, '', 'AI feedback:']
                      for (const k of ['time_fit', 'budget', 'equipment', 'skills', 'specificity'] as const) {
                        const d = proposeEval?.[k]
                        if (d) parts.push(`- ${k}: ${d.state ?? d.rating} — ${d.note ?? d.explanation ?? ''}`)
                      }
                      if (proposeEval?.step2_hint) parts.push('', proposeEval.step2_hint)
                      return parts.join('\n')
                    })()}
                  />
                </Box>

                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Does this feel like the right direction for you?
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained" color="success" size="small"
                    startIcon={<ThumbUpIcon />}
                    disabled={!proposedRec || acceptedTopics.length >= 3}
                    onClick={() => {
                      if (!proposedRec) return
                      const flagged = (['time_fit', 'budget', 'equipment', 'skills', 'specificity'] as const)
                        .filter(k => proposeEval?.[k]?.state === 'thinking')
                      if (flagged.length > 0) {
                        setOverruleOpen({ kind: 'student', rec: proposedRec, states: flagged })
                      } else {
                        acceptProposed(proposedRec)
                      }
                    }}
                  >
                    Add to my 3 topics
                  </Button>
                  <Button
                    variant="text" size="small"
                    onClick={() => { setProposeEval(null); setProposeLoading(false) }}
                  >
                    Revise idea
                  </Button>
                  <Button variant="outlined" color="error" size="small" startIcon={<ThumbDownIcon />}
                    onClick={async () => {
                      if (proposedRec) {
                        await supabase.from('topic_recommendation').update({
                          decision: 'reject', justification: 'Discarded after evaluation', decided_at: new Date().toISOString(),
                        }).eq('id', proposedRec.id)
                      }
                      resetPropose()
                    }}
                  >
                    Discard
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* ── AI SUGGESTIONS (secondary, collapsible) ──────────────────── */}
        <Box
          onClick={() => setAiSuggestionsOpen(o => !o)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            cursor: 'pointer', mb: aiSuggestionsOpen ? 2 : 3,
            color: 'text.secondary', fontSize: '0.85rem', fontWeight: 600,
          }}
        >
          {aiSuggestionsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          Stuck? Browse AI suggestions
        </Box>

        {/* Current recommendation — only when AI suggestions panel is expanded */}
        {aiSuggestionsOpen && error && (
          <Alert severity="error" sx={{ mb: 2 }} action={
            <Button color="inherit" size="small" onClick={() => { setError(null); fetchRecommendations() }}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        )}

        {aiSuggestionsOpen && !proposeEval && !error && loading && !currentRec && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={28} sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Finding your next suggestion...</Typography>
            <Button variant="text" size="small" onClick={() => { abortRef.current?.abort(); setLoading(false) }}>
              Cancel
            </Button>
          </Box>
        )}

        {aiSuggestionsOpen && !proposeEval && !error && !loading && !currentRec && acceptedTopics.length < 3 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No suggestions loaded.</Typography>
            <Button variant="outlined" onClick={() => fetchRecommendations()}>Load Suggestions</Button>
          </Box>
        )}

        {aiSuggestionsOpen && !proposeEval && currentRec && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {currentRec.spanning_categories?.map(c => (
                  <Chip key={c} label={c} size="small" variant="outlined" color="primary" />
                ))}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{currentRec.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {currentRec.description}
              </Typography>

              {deciding === currentRec.id ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    {decidingAction === 'accept'
                      ? 'What draws you to this topic?'
                      : 'Why isn\'t this a good fit for you?'}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder={
                      decidingAction === 'accept'
                        ? 'Describe a specific connection — experience, question, or curiosity...'
                        : 'Be honest — this helps the AI find something better...'
                    }
                    value={decisionText}
                    onChange={e => setDecisionText(e.target.value)}
                    sx={{ mb: 2 }}
                    helperText={`${decisionText.trim().length} / 20 characters minimum`}
                    error={decisionText.trim().length > 0 && decisionText.trim().length < 20}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      color={decidingAction === 'accept' ? 'success' : 'error'}
                      startIcon={decidingAction === 'accept' ? <ThumbUpIcon /> : <ThumbDownIcon />}
                      disabled={decisionText.trim().length < 20 || loading}
                      onClick={() => handleDecision(currentRec, decidingAction!)}
                    >
                      {loading
                        ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Saving...</>
                        : decidingAction === 'accept' ? 'Confirm' : 'Pass on this one'
                      }
                    </Button>
                    <Button variant="text" onClick={() => { setDeciding(null); setDecidingAction(null); setDecisionText('') }}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {acceptedTopics.length >= 3 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      You've reached your 3-topic shortlist. Remove one above (tap its ✕) to add this one.
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ThumbUpIcon />}
                      disabled={acceptedTopics.length >= 3}
                      onClick={() => { setDeciding(currentRec.id); setDecidingAction('accept'); setDecisionText('') }}
                    >
                      This interests me
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<ThumbDownIcon />}
                      onClick={() => { setDeciding(currentRec.id); setDecidingAction('reject'); setDecisionText('') }}
                    >
                      Not for me
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Or ask the AI for a variation:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" disabled={loading}
                      onClick={() => iterateOnTopic(currentRec, 'fresh')}>
                      Fresh idea
                    </Button>
                    <Button size="small" variant="outlined" disabled={loading}
                      onClick={() => iterateOnTopic(currentRec, 'harder')}>
                      Make harder
                    </Button>
                    <Button size="small" variant="outlined" disabled={loading}
                      onClick={() => iterateOnTopic(currentRec, 'simpler')}>
                      Make simpler
                    </Button>
                    <Button size="small" variant="outlined" disabled={loading}
                      onClick={() => iterateOnTopic(currentRec, 'different_angle')}>
                      Different angle
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Prior decisions */}
        {recommendations.filter(r => r.decision !== null).length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="overline" color="text.secondary">Your decisions so far</Typography>
            {recommendations.filter(r => r.decision !== null).reverse().map(r => (
              <Box
                key={r.id}
                sx={{
                  display:      'flex',
                  alignItems:   'flex-start',
                  gap:          1,
                  py:           1,
                  borderBottom: '1px solid',
                  borderColor:  'divider',
                }}
              >
                {r.decision === 'accept'
                  ? <ThumbUpIcon fontSize="small" color="success" />
                  : <ThumbDownIcon fontSize="small" color="error" />
                }
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.justification}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Escape hatch — return to categories */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {restartConfirmPanel || (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="text"
                size="small"
                sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
                onClick={() => setShowRestartConfirm(true)}
                disabled={loading}
              >
                ← Change my research categories
              </Button>
            </Box>
          )}
        </Box>

        {devClearButton}

        {/* Overrule confirmation — when student accepts a direction the AI flagged */}
        <Dialog
          open={!!overruleOpen}
          onClose={() => setOverruleOpen(null)}
          maxWidth="xs" fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Take this on anyway?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              The AI flagged this topic as worth more thought on:{' '}
              <strong>
                {(overruleOpen?.states ?? []).map(s => DIM_LABELS[s] ?? s).join(', ')}
              </strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You're choosing to take it on anyway — that's your call. Make sure you've thought
              through how you'll handle the flagged areas.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOverruleOpen(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                const r = overruleOpen?.rec
                setOverruleOpen(null)
                if (r) acceptProposed(r)
              }}
            >
              Add it anyway
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  }

  // ── Final pick — status-aware: draft / submitted / completed ──────────────
  if (phase === 'final') {
    // A pending mentor meeting (requested/scheduled, not yet held) no longer
    // locks the student out — they may keep improving their topics or explore
    // new ones while they wait, if they choose. The submission and the meeting
    // both stand; nothing is discarded.
    const hasPendingMeeting = !!(meetingScheduledAt || meetingRequestedAt)
    const isReadOnly = submissionStatus === 'submitted' && !hasPendingMeeting
    // Hard-locked submitted state shows only the submitted finalists. When
    // editable (draft, or submitted-with-pending-meeting) show all accepted.
    const visibleTopics = isReadOnly
      ? acceptedTopics.filter(t => pickedFinalistIds.includes(t.id))
      : acceptedTopics
    const isReturning = !isReadOnly && !hasPendingMeeting && (mentorNote || meetingHeldAt)

    return (
      <Box sx={{ maxWidth: 780, py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {isReadOnly ? 'Submitted for Mentor Review' : 'Submit Your Topic Finalists'}
        </Typography>
        {!isReadOnly && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Pick <strong>1</strong> if you're sure. Pick <strong>more</strong> if you want to talk it
            through with your mentor before deciding. The AI's read on each topic is informational —
            <strong> you decide</strong> the risk/reward.
          </Typography>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Awaiting-review banner (status='submitted'). Shows three sub-states
            based on meeting workflow: scheduled > requested > plain awaiting. */}
        {submissionStatus === 'submitted' && meetingScheduledAt && (
          <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
            <strong>Mentor meeting scheduled:</strong>{' '}
            {new Date(meetingScheduledAt).toLocaleString('en-US', {
              weekday: 'short', month: 'long', day: 'numeric',
              hour: 'numeric', minute: '2-digit',
              timeZone: 'America/Los_Angeles',
            })}
            {mentorNote && (
              <Box sx={{ mt: 1 }}>
                <strong>Note from your mentor:</strong>
                <Markdown>{mentorNote}</Markdown>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Come ready to discuss your finalists. You don't have to wait — you can keep improving
              your topics or explore new ones below. Your submission and meeting still stand, and any
              changes you make are saved.
            </Typography>
          </Alert>
        )}
        {submissionStatus === 'submitted' && !meetingScheduledAt && meetingRequestedAt && (
          <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
            <strong>Your mentor would like to meet about this submission.</strong>{' '}
            The office is contacting your parent to find a time, and you'll see the details here once
            it's scheduled.
            {mentorNote && (
              <Box sx={{ mt: 1 }}>
                <strong>Note from your mentor:</strong>
                <Markdown>{mentorNote}</Markdown>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              You don't have to wait — keep improving your topics or explore new ones below. Your
              submission and the meeting still stand, and any changes you make are saved.
            </Typography>
          </Alert>
        )}
        {isReadOnly && !meetingScheduledAt && !meetingRequestedAt && (
          <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
            <strong>Submitted to your mentor for review.</strong>{' '}
            {pickedFinalistIds.length === 1
              ? "They'll either approve your final pick or send it back with feedback."
              : "They'll respond with feedback. You'll come back here to refine and resubmit until you're ready to commit to one."}
          </Alert>
        )}

        {/* Returning-from-review banner (status='draft' with mentor_note or held meeting) */}
        {isReturning && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>
              {meetingHeldAt
                ? `Notes from your meeting (${new Date(meetingHeldAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}):`
                : 'Your mentor sent this back:'}
            </strong>
            {mentorNote && <Box sx={{ mt: 0.5 }}><Markdown>{mentorNote}</Markdown></Box>}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Refine your finalists and resubmit when you're ready.
            </Typography>
          </Alert>
        )}

        {/* Whenever the submission is still editable (draft, or submitted-with-
            pending-meeting), let the student jump back to the topic loop to add or
            swap topics. Only the hard-locked submitted state hides this. Without
            it, a student on the final screen (e.g. after reload, or after
            re-opening an approved topic) has no way to add a new topic. */}
        {!isReadOnly && (
          <Box sx={{ mb: 3 }}>
            <Button variant="outlined" onClick={() => setPhase('loop')}>
              Explore more topics
            </Button>
          </Box>
        )}

        {/* ── Topic cards ── */}
        {visibleTopics.map(rec => {
          const isPicked = pickedFinalistIds.includes(rec.id)
          // Pull AI evaluation from topic_recommendation.evaluation for student-proposed topics
          const evalData = (rec as any).evaluation as Record<string, any> | null
          const hasFiveDim = evalData && ['time_fit', 'budget', 'equipment', 'skills', 'specificity'].some(k => evalData[k])
          return (
            <Card
              key={rec.id}
              variant="outlined"
              sx={{
                mb:          2,
                cursor:      isReadOnly ? 'default' : 'pointer',
                border:      isPicked ? '2px solid' : '1px solid',
                borderColor: isPicked ? 'primary.main' : 'divider',
                transition:  'all 0.15s',
                '&:hover':   isReadOnly ? {} : {
                  borderColor: isPicked ? 'primary.main' : 'primary.light',
                  bgcolor:     isPicked ? 'transparent' : 'rgba(25,118,210,0.03)',
                },
              }}
              onClick={() => {
                if (isReadOnly) return
                setPickedFinalistIds(prev =>
                  prev.includes(rec.id) ? prev.filter(id => id !== rec.id) : [...prev, rec.id]
                )
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  {isPicked
                    ? <CheckCircleIcon color="primary" sx={{ fontSize: 20 }} />
                    : <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: 'text.disabled' }} />}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>{rec.title}</Typography>
                  {!isReadOnly && (
                    <Button
                      size="small"
                      variant="text"
                      color="inherit"
                      sx={{ fontSize: '0.75rem', color: 'text.secondary', flexShrink: 0 }}
                      onClick={(e) => { e.stopPropagation(); setReconsiderTarget(rec) }}
                    >
                      Reconsider
                    </Button>
                  )}
                </Box>

                {/* Research category this topic belongs to */}
                {(rec.spanning_categories ?? []).filter(Boolean).length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    {(rec.spanning_categories ?? []).filter(Boolean).map(c => (
                      <Chip key={c} size="small" label={c} color="secondary" variant="outlined" />
                    ))}
                  </Box>
                )}

                {/* Description */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.65 }}>
                  {rec.description}
                </Typography>

                {/* Per-topic justification — only when picked. Sits between the
                    description and the AI score. Two questions: why this one, and
                    if AI flagged anything, how the student plans to handle it. */}
                {isPicked && !isReadOnly && (() => {
                  const flagged = (['time_fit', 'budget', 'equipment', 'skills', 'specificity'] as const)
                    .filter(k => {
                      const s = evalData?.[k]?.state
                      return s === 'caution' || s === 'thinking'
                    })
                    .map(k => DIM_LABELS[k] ?? k)
                  const hasConcerns = flagged.length > 0
                  const text = finalistThoughts[rec.id] ?? ''
                  const tooShort = text.trim().length > 0 && text.trim().length < 30
                  return (
                    <Box sx={{ mb: 1.5 }} onClick={e => e.stopPropagation()}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.5 }}>
                        Why this one?
                      </Typography>
                      {hasConcerns && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontStyle: 'italic' }}>
                          The AI flagged <strong>{flagged.join(', ')}</strong> below. How do you plan to handle {flagged.length === 1 ? 'it' : 'them'}?
                        </Typography>
                      )}
                      <TextField
                        multiline
                        rows={3}
                        fullWidth
                        size="small"
                        value={text}
                        onChange={e => setFinalistThoughts(prev => ({ ...prev, [rec.id]: e.target.value }))}
                        placeholder={
                          hasConcerns
                            ? "I'm picking this because… To handle the AI's concern about [X], I'll…"
                            : "I'm picking this because…"
                        }
                        helperText={
                          tooShort
                            ? `${text.trim().length} / 30 minimum`
                            : text.trim().length >= 30
                              ? 'Good — your mentor will read this'
                              : null
                        }
                        error={tooShort}
                      />
                    </Box>
                  )
                })()}

                {/* When read-only (submitted), display the saved justification */}
                {isPicked && isReadOnly && (finalistThoughts[rec.id] || (rec as any).finalist_thoughts) && (
                  <Box sx={{ mb: 1.5, p: 1.25, bgcolor: 'rgba(25,118,210,0.04)', borderRadius: 1, border: '1px solid', borderColor: 'primary.light' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.5 }}>
                      Your reasoning
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {finalistThoughts[rec.id] ?? (rec as any).finalist_thoughts}
                    </Typography>
                  </Box>
                )}

                {/* AI 5-dim feasibility — present when evaluation has been run */}
                {hasFiveDim && (
                  <Box sx={{ mb: 1.5 }}>
                    {(['time_fit', 'budget', 'equipment', 'skills', 'specificity'] as const).map(key => {
                      const d = evalData?.[key]
                      if (!d) return null
                      return <DimRow key={key} dimKey={key} d={d} />
                    })}
                  </Box>
                )}

                {(evalData?.research_questions ?? []).length > 0 && (
                  <ResearchQuestions questions={evalData!.research_questions} />
                )}

                {/* Eval on demand — for topics that don't have one yet */}
                {!hasFiveDim && !isReadOnly && (
                  <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.02)', border: '1px dashed', borderColor: 'divider' }} onClick={e => e.stopPropagation()}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, lineHeight: 1.55 }}>
                      No AI feedback on this topic yet. Get a quick read on time fit, budget, equipment, skills, and specificity before you commit.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => evaluateTopicOnDemand(rec)}
                      disabled={evaluatingTopicId === rec.id}
                    >
                      {evaluatingTopicId === rec.id
                        ? <><CircularProgress size={14} sx={{ mr: 1 }} />Evaluating…</>
                        : 'Get AI feedback'}
                    </Button>
                  </Box>
                )}

                {/* ── Inline Q&A — disabled in read-only (submitted) state ── */}
                {!isReadOnly && <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                  onClick={e => e.stopPropagation()}
                >
                  {/* Prior Q&A history */}
                  {(askHistory[rec.id] ?? []).map((qa, i) => (
                    <Box key={i} sx={{ mb: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block' }}>
                        Q: {qa.question}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.75 }}>
                        <Box sx={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                          bgcolor: 'primary.main', color: 'white',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.6rem', fontWeight: 800,
                        }}>AI</Box>
                        <Box sx={{ bgcolor: 'rgba(25,118,210,0.05)', border: '1px solid', borderColor: 'primary.light', borderRadius: 1.5, p: 1.25, flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
                            {qa.answer}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                            <SaveToJournalButton
                              projectId={projectId}
                              stepNumber={1}
                              sourceLabel={`AI Q&A — ${rec.title}`}
                              content={`Q: ${qa.question}\nA: ${qa.answer}`}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}

                  {/* Input — always visible once opened, stays open */}
                  {askingTopicId === rec.id ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        maxRows={3}
                        placeholder="Ask another question..."
                        value={askInput}
                        onChange={e => setAskInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askQuestion(rec) } }}
                        disabled={askLoading}
                        autoFocus
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => askQuestion(rec)}
                        disabled={!askInput.trim() || askLoading}
                        sx={{ flexShrink: 0, mt: 0.25 }}
                      >
                        {askLoading ? <CircularProgress size={16} color="inherit" /> : 'Ask'}
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}
                      onClick={() => { setAskingTopicId(rec.id); setAskInput('') }}
                    >
                      {(askHistory[rec.id]?.length ?? 0) > 0 ? '+ Ask another question' : 'Have a question about this topic?'}
                    </Button>
                  )}
                </Box>}
              </CardContent>
            </Card>
          )
        })}

        {!isReadOnly && restartConfirmPanel}

        {!isReadOnly && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            {!showRestartConfirm && (
              <Button
                variant="text"
                size="small"
                sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
                onClick={() => setShowRestartConfirm(true)}
                disabled={submitting}
              >
                ← Change my research categories
              </Button>
            )}
            <Box sx={{ ml: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmitFinalists}
                disabled={
                  submitting ||
                  !viewerIsStudent ||
                  pickedFinalistIds.length < 1 ||
                  pickedFinalistIds.some(id => (finalistThoughts[id] ?? '').trim().length < 30)
                }
                sx={{ px: 5 }}
              >
                {submitting
                  ? <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Submitting...</>
                  : pickedFinalistIds.length === 1
                    ? 'Submit final pick for approval →'
                    : `Submit ${pickedFinalistIds.length} finalists for mentor review →`
                }
              </Button>
              {!viewerIsStudent && (
                <Typography variant="caption" color="text.secondary">
                  Only the student can submit — you're viewing as them.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Reconsider confirmation — explicit warning before clearing work */}
        <Dialog
          open={!!reconsiderTarget}
          onClose={() => setReconsiderTarget(null)}
          maxWidth="xs" fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Reconsider this topic?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              You're about to send <strong>"{reconsiderTarget?.title}"</strong> back to your shortlist
              for another look.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              When you do:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                This topic loses its "accepted" status — you'll need to add a replacement (or this one back) before you can submit.
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Anything you've typed in <em>"What I'm thinking"</em> will be cleared.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setReconsiderTarget(null)}>Cancel</Button>
            <Button
              variant="contained" color="warning"
              onClick={async () => {
                const r = reconsiderTarget
                if (!r) return
                setReconsiderTarget(null)
                await supabase.from('topic_recommendation')
                  .update({ decision: null, justification: null, decided_at: null })
                  .eq('id', r.id)
                setRecommendations(prev => prev.map(rr =>
                  rr.id === r.id ? { ...rr, decision: null, justification: null } : rr
                ))
                if (finalPickId === r.id) setFinalPickId(null)
                // Drop r from picked finalists if present
                setPickedFinalistIds(prev => prev.filter(id => id !== r.id))
                setFinalJustify('')
                setComparison(null)
                setSubmissionStatus(null)
                setPhase('loop')
              }}
            >
              Yes, reconsider
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  }

  return null
}


// ── Main page ──────────────────────────────────────────────────────────────────

export default function Step1Page() {
  const { session, studentLifecycle } = useAuth()
  const { studentId: viewerStudentId, viewerIsStudent, prefixPath, loading: vasLoading } = useViewerStudent()
  const navigate    = useNavigate()
  const isTrialExpired = studentLifecycle?.lifecycle_state === 'trial_expired'

  const [project,   setProject]   = useState<Project | null>(null)
  const [studentId, setStudentId] = useState<string | null>(null)
  const [loading,   setLoading]   = useState(true)
  const [activeTab, setActiveTab] = useState(0)
  const [gatePassed, setGatePassed] = useState(false)
  const [isTrial,    setIsTrial]    = useState(false)
  const [projectErr, setProjectErr] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      if (!session || vasLoading) return
      if (!viewerStudentId) { navigate('/'); return }

      const { data: sp } = await supabase
        .from('student_profile')
        .select('student_id, subscription_status')
        .eq('student_id', viewerStudentId)
        .maybeSingle()
      if (!sp) { navigate('/'); return }
      setStudentId(sp.student_id)
      setIsTrial(sp.subscription_status === 'free')

      const fetchProject = async () => (await supabase
        .from('project').select('*').eq('student_id', sp.student_id).eq('status', 'active').limit(1)).data?.[0] ?? null
      let project = await fetchProject()
      // Some sign-up routes leave a student without a project, and students
      // can't create one under RLS — so ask the server, then read it back.
      if (!project) {
        const { data: ens, error: ensErr } = await supabase.functions.invoke('ensure-project', { body: { student_id: sp.student_id } })
        if (ensErr || ens?.error) setProjectErr(ensErr ? await readFunctionError(ensErr) : ens.error)
        else project = await fetchProject()
      }
      setProject(project)

      if (project) {
        const { data: ci } = await supabase
          .from('category_input').select('gate_passed').eq('project_id', project.project_id).maybeSingle()
        if (ci?.gate_passed) {
          setGatePassed(true)
          // Do NOT auto-switch tabs — student navigates intentionally
        }
      }
      setLoading(false)
    }
    load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, viewerStudentId, vasLoading])

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
  if (projectErr) return <Box sx={{ p: 4 }}><Alert severity="error">We couldn't set up your project: {projectErr}. Please refresh, or contact your mentor.</Alert></Box>
  if (!project || !studentId) return null

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate(prefixPath('/'))} size="small"><ArrowBackIcon /></IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Step 1: Select a Research Topic</Typography>
          <Typography variant="body2" color="text.secondary">
            Watch the lessons, select your research categories, then explore and pick a topic.
          </Typography>
        </Box>
      </Box>

      {isTrialExpired && (
        <Box sx={{
          mb: 3, px: 2, py: 1.5, borderRadius: 2,
          bgcolor: 'error.lighter', border: '1px solid', borderColor: 'error.light',
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
            Trial expired — read-only mode
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can review your existing work, but new submissions are paused. Contact your office to enroll and continue.
          </Typography>
        </Box>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tab label="Learn" />
        <Tab label="Research Category" />
        <Tab label={isTrial ? 'Topic Preview' : 'Research Topic'} disabled={!gatePassed} />
        <Tab label="Journal" disabled={!gatePassed} />
        <Tab label="Mentor Session" />
      </Tabs>

      {activeTab === 0 && <LearnTab studentId={studentId} />}

      {/* ResearchFlow: shared state for category (tab 1) and full topic (tab 2, paid only) */}
      {activeTab !== 0 && activeTab !== 3 && activeTab !== 4 && !isTrial && (
        <ResearchFlow
          projectId={project.project_id}
          studentId={studentId}
          activeView={activeTab === 1 ? 'category' : 'topic'}
          onGatePassed={() => setGatePassed(true)}
          onAdvanceToTopic={() => setActiveTab(2)}
        />
      )}

      {/* Trial: category tab uses ResearchFlow, topic tab uses DemoTopicsTab */}
      {isTrial && activeTab === 1 && (
        <ResearchFlow
          projectId={project.project_id}
          studentId={studentId}
          activeView="category"
          onGatePassed={() => setGatePassed(true)}
          onAdvanceToTopic={() => setActiveTab(3)}
          isTrial
        />
      )}
      {isTrial && activeTab === 2 && gatePassed && (
        <DemoTopicsTab projectId={project.project_id} studentId={studentId} />
      )}

      {activeTab === 3 && (
        <JournalTab projectId={project.project_id} studentId={studentId} isTrial={isTrial} viewerIsStudent={viewerIsStudent} />
      )}

      {activeTab === 4 && (
        <MentorSessionTab projectId={project.project_id} studentId={studentId} />
      )}
    </Box>
  )
}

// ── Summary card ───────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function SummaryCard({ summary }: { summary: Summary }) {
  const trackLabel: Record<string, string> = {
    scientific_method:  'Scientific Method',
    engineering_design: 'Engineering Design',
  }
  const typeLabel: Record<string, string> = {
    physical:           'Physical',
    biological:         'Biological',
    chemical:           'Chemical',
    data_computational: 'Data / Computational',
  }
  const scopeLabel: Record<string, string> = {
    single_year: 'Single Year',
    two_year:    'Two Year',
  }
  return (
    <Card variant="outlined">
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Research Topic</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{summary.confirmed_topic}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={trackLabel[summary.confirmed_track] ?? summary.confirmed_track} color="primary" size="small" />
          <Chip label={typeLabel[summary.confirmed_project_type] ?? summary.confirmed_project_type} size="small" />
          <Chip label={scopeLabel[summary.confirmed_scope] ?? summary.confirmed_scope} size="small" variant="outlined" />
          <Chip label={summary.confirmed_intent === 'competition' ? 'Competition Track' : 'Completion Track'}
            color={summary.confirmed_intent === 'competition' ? 'success' : 'default'} size="small" />
        </Box>
        <Divider />
        <Box>
          <Typography variant="caption" color="text.secondary">AI Rationale</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{summary.ai_rationale}</Typography>
        </Box>
        <Box sx={{ bgcolor: 'primary.50', borderRadius: 1, p: 2 }}>
          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>What's next — Step 2</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{summary.step2_preview}</Typography>
        </Box>
      </CardContent>
    </Card>
  )
}
